/**
 * Compliance Service
 *
 * Service for managing compliance issues and detailed compliance data.
 * Provides methods for loading, filtering, and managing compliance issues.
 */

import { api } from '@/lib/api';

/**
 * Utility function to check if an error is a request cancellation
 * Handles both native AbortError and Axios CanceledError
 */
export function isRequestCancelled(error: unknown): error is Error {
  return (
    error instanceof Error &&
    (error.name === 'AbortError' ||
     error.name === 'CanceledError' ||
     (error as any).code === 'ERR_CANCELED')
  );
}
import { toast } from 'sonner';
import {
  validateComplianceResponse,
  validateLegacyComplianceResponse,
  isValidIssueHash,
  validateAndFilterIssues,
  createFallbackComplianceResponse,
  createFallbackLegacyComplianceResponse
} from '../utils/validation/compliance-validation';
import { SecureStorage } from '../utils/secure-storage';
import { VENDOR_HISTORY_CONFIG } from '../types/vendor-history.types';
import {
  retryWithBackoff,
  classifyError,
  retryFailedItems,
  ErrorType,
  generateOperationId,
  persistRetryState,
  getPersistedRetryState,
  clearPersistedRetryState,
  type BulkRetryResult,
  type PersistedRetryState
} from '../utils/error-recovery.utils';

export interface LineItemDetail {
  line_item_id?: string; // Line item ID for validation (optional with fallbacks)
  line_index: number; // Line item index (0-based)
  tax_type?: string; // VAT/tax type (e.g., "STANDARD", "EXEMPT", "CAPEXINPUT") - defaults to 'STANDARD'
  line_amount: number; // Line item amount
  description?: string; // Line item description - can be undefined
  account_code?: string; // Account code for the line item - can be undefined
  // Enhanced fields from Chart of Accounts and tax information
  account_name?: string | null; // Account name from Chart of Accounts
  formatted_account?: string | null; // Formatted "code - name" display
  tax_amount?: number | null; // Tax amount for this line item
  formatted_tax?: string | null; // Formatted tax display (e.g., "STANDARD - $45.00")
  quantity?: number | null; // Quantity (optional)
  unit_amount?: number | null; // Unit amount (optional)
  tracking?: Array<{
    tracking_category_id?: string;
    tracking_category_name?: string;
    tracking_option_id?: string;
    tracking_option_name?: string;
  }>; // Tracking categories (optional)
}

export interface ComplianceIssue {
  issue_hash: string; // 64-character SHA-256 hash (accepts 32-char legacy hashes during migration)
  issue_type: string;
  type: string; // Alias for issue_type for compatibility
  severity: 'HIGH' | 'MEDIUM' | 'LOW';
  status: 'review' | 'fix' | 'ok';
  title: string;
  description: string;
  source_id?: string;
  source_type?: string;
  xero_transaction_id?: string; // Actual Xero transaction ID (for building Xero deep links)
  entity_id?: string; // Entity this issue belongs to
  entity_name?: string; // Entity name for display
  contact_id?: string; // Contact/vendor ID from source system (for exemptions)
  contact_name?: string; // Contact/vendor name for display
  source_transaction_reference?: string; // Transaction reference from source system
  source_transaction_date?: string; // Transaction date from source system
  total_amount?: number; // Transaction amount (NEW - MVP backend enhancement)
  currency_code?: string; // Transaction currency (NEW - MVP backend enhancement)
  related_transaction_ids?: string[]; // For duplicate issues
  detected_at?: string;
  first_detected_at?: string;
  detection_count?: number;
  // Line item specific fields (A1/A2 issues)
  line_index?: number; // Which line item (0-indexed) for A2 issues
  line_description?: string; // Line item description for A2 issues
  affected_line_items?: LineItemDetail[]; // Array of line items for A1 issues
  metadata?: {
    linked_invoice_id?: string; // Type H: UUID of linked bill/invoice
    [key: string]: any;
  };
  // Clean MVP status tracking - NO LEGACY FIELDS
  last_status_change?: string; // When status was last changed
  last_changed_by?: string; // Who last changed the status
  last_message?: string; // Plain text version of the most recent action message
  // Snooze functionality fields
  snooze_until_date?: string | null; // ISO timestamp when snooze expires
  snoozed_at?: string | null; // ISO timestamp when issue was snoozed
  snoozed_by?: string | null; // User email who snoozed the issue
  snooze_reason?: string | null; // Optional reason for snoozing
  snooze_count?: number; // Number of times this issue has been snoozed
}

export interface ComplianceFilters {
  types: string[];
  severities: Array<'HIGH' | 'MEDIUM' | 'LOW'>;
  status: Array<'review' | 'fix' | 'ok'>;
  entityIds: string[];
}

export interface EntitySummary {
  entity_id: string;
  entity_name: string;
  total_issues: number;
  high_severity: number;
  medium_severity: number;
  low_severity: number;
  types: Record<string, number>;
}

export interface ComplianceMatrixSummary {
  entity_id: string;
  date_range: {
    from: string | null;
    to: string | null;
  };
  total_issues: number;
  matrix: Record<string, Record<string, {
    count: number;
    highest_severity: 'HIGH' | 'MEDIUM' | 'LOW';
    latest_date: string | null;
  }>>;
  summary: {
    total_months: number;
    active_issue_types: string[];
    severity_breakdown: {
      HIGH: number;
      MEDIUM: number;
      LOW: number;
    };
    processing_time_ms: number;
    issues_processed: number;
    filtered_issues_count: number;
    memory_usage_mb?: number | null;
    memory_monitoring_enabled: boolean;
    filters_applied: {
      issue_types: string[] | null;
      severity: string[] | null;
      status: string[] | null;
      date_from: string | null;
      date_to: string | null;
    };
  };
}

export interface ComplianceResponse {
  entity_id: string;
  active_issues: ComplianceIssue[];
  dismissed_issues: ComplianceIssue[];
}

// Legacy response format for backward compatibility
export interface LegacyComplianceResponse {
  success: boolean;
  entity_id?: string;
  client_id?: string;
  total_issues: number;
  filtered_issues: number;
  entities?: Array<{ entity_id: string; entity_name: string }>;
  entities_with_issues?: number;
  applied_filters: Partial<ComplianceFilters> & {
    limit: number;
    offset: number;
  };
  pagination: {
    total: number;
    page: number;
    per_page: number;
    pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
  issues: ComplianceIssue[];
  entities_summary?: EntitySummary[];
}

export interface ComplianceRequestParams {
  client_id?: string;
  entity_id?: string;
  issue_hash?: string; // For deep linking - fetch single issue
  issue_types?: string[];
  severity?: string[];
  status?: string[];
  date_from?: string;
  date_to?: string;
  entity_ids?: string[];
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
  _t?: number; // Cache busting parameter
}

export interface RelatedIssuesOptions {
  exclude_current?: boolean;
  include_statuses?: ('review' | 'fix' | 'ok')[];
  limit?: number;
}

// Unified related item that can be compliance, AML, or prepayment
export interface RelatedItem {
  id: string;
  source_type: 'compliance' | 'aml' | 'prepayment';
  issue_type: string;
  severity: string;
  status: string;
  title: string;
  description: string;
  entity_id: string;
  contact_name?: string;
  contact_id?: string;
  source_id?: string;
  source_transaction_reference?: string;
  source_transaction_date?: string;
  total_amount?: number;
  currency_code?: string;
  detected_at?: string;
  // Prepayment-specific fields
  amortization_start_date?: string;
  amortization_end_date?: string;
  number_of_periods?: number;
}

export interface RelatedIssuesResponse {
  success: boolean;
  entity_id: string;
  source_id: string;
  related_issues: RelatedItem[];
  active_issues: RelatedItem[];
  closed_issues: RelatedItem[];
  total_related: number;
  counts?: {
    compliance: number;
    aml: number;
    prepayment: number;
  };
  matching_criteria?: {
    source_id: string;
  };
  partial_results?: boolean;
  failed_sources?: string[];
}

export interface BulkOperationResult {
  success_count: number;
  failure_count: number;
  total_count: number;
  results: Array<{
    issue_hash: string;
    success: boolean;
    error_message?: string;
  }>;
  overall_success: boolean;
}

export interface BulkOperationProgress {
  completed: number;
  total: number;
  current_chunk: number;
  total_chunks: number;
  errors: any[];
}

export interface EnhancedBulkOperationResult extends BulkOperationResult {
  operation_id?: string;
  retryable_failures?: Array<{
    issue_hash: string;
    error_type: ErrorType;
    can_retry: boolean;
    retry_after?: number;
  }>;
  retry_info?: {
    total_retries: number;
    max_retries_reached: boolean;
    next_retry_delay?: number;
  };
}

export interface RetryOperationOptions {
  operationId?: string;
  onProgress?: (completed: number, total: number, errors: any[]) => void;
  signal?: AbortSignal;
  maxRetries?: number;
}

export interface AttachmentMetadata {
  attachment_id: string;
  file_name: string;
  mime_type: string;
  file_size: number;
  gcs_upload_status?: string;
  llm_processed: boolean;
  created_at: string;
  lazy_loaded?: boolean;
}

/**
 * Type C: Duplicate Bills Metadata
 * Represents duplicate bill detection with primary/duplicate classification
 */
export interface DuplicateBillsMetadata {
  type: 'DUPLICATE_BILLS';
  contact_id: string;
  contact_name: string;
  transaction_reference: string;
  xero_url: string;
  source_transaction_date: string; // YYYY-MM-DD format
  amount: number;
  currency_code: string;
  description: string;
  line_items?: string[];
  has_attachments?: boolean;
  status?: string;

  // Duplicate classification fields
  is_primary?: boolean; // True if this is the original transaction, false if duplicate
  position_in_group?: string; // Position description (e.g., "1 of 3", "2 of 3")
  duplicate_count?: number; // Total number of duplicates including original
  primary_transaction_date?: string; // Date of the primary/original transaction (YYYY-MM-DD)
}

/**
 * Type F: Aged Payables Metadata
 * Represents overdue payable invoices requiring attention
 */
export interface AgedPayableMetadata {
  type: 'AGED_PAYABLE';
  contact_id: string;
  contact_name: string;
  transaction_reference: string;
  xero_url: string;
  source_transaction_date: string; // YYYY-MM-DD format
  due_date: string; // YYYY-MM-DD format
  days_overdue: number;
  amount_due: number;
  currency_code: string;
  description: string;
  line_items?: string[];
  has_attachments?: boolean;
  status?: string;
}

/**
 * Type G: Aged Receivables Metadata
 * Represents overdue receivable invoices requiring attention
 */
export interface AgedReceivableMetadata {
  type: 'AGED_RECEIVABLE';
  contact_id: string;
  contact_name: string;
  transaction_reference: string;
  xero_url: string;
  source_transaction_date: string; // YYYY-MM-DD format
  due_date: string; // YYYY-MM-DD format
  days_overdue: number;
  amount_due: number;
  currency_code: string;
  description: string;
  line_items?: string[];
  has_attachments?: boolean;
  status?: string;
}

/**
 * Type H: Unreconciled Bank Transactions
 * Represents bank transactions that have not been reconciled
 */
export interface UnreconciledMetadata {
  type: 'UNRECONCILED';
  contact_id: string;
  contact_name: string;
  transaction_reference: string;
  xero_url: string;
  source_transaction_date: string; // YYYY-MM-DD format
  amount: number;
  currency_code: string;
  description: string;
  line_items?: string[]; // Array of line descriptions (optional, may be empty)
  bank_account_id: string;
  bank_account_name: string;
  transaction_type: string; // "SPEND" or "RECEIVE"
  is_reconciled: boolean;
  has_attachments: boolean;
  status: string;
}

/**
 * Type I: Serial Checker Metadata
 * Represents invoice/bill reference number sequence anomalies for manual review
 */
export interface SerialCheckerMetadata {
  type: 'SERIAL_CHECK';
  contact_id: string;
  contact_name: string;
  transaction_reference: string;
  document_type: string; // "ACCPAY", "ACCPAYCREDIT", or "SPEND"
  xero_url: string;
  source_transaction_date: string; // YYYY-MM-DD format
  subtotal: number;
  tax: number;
  tax_percent: string; // e.g., "20.0%"
  total_amount: number;
  currency_code: string;
  description: string;
  line_items?: string[]; // Array of line descriptions (optional)
  has_attachments?: boolean;
  status?: string;

  // Anomaly-specific fields
  anomaly_type?: 'gap' | 'duplicate' | 'inversion';

  // Gap-specific fields
  gap_position?: 'before' | 'after';
  gap_size?: number;
  before_ref?: string;
  after_ref?: string;
  missing_range?: string;

  // Duplicate-specific fields
  duplicate_count?: number;
  reference?: string;
  duplicate_dates?: string[]; // ISO date strings

  // Inversion-specific fields
  earlier_ref?: string;
  later_ref?: string;
  earlier_date?: string; // ISO date string
  later_date?: string; // ISO date string
  time_delta_days?: number;
}

export interface TransactionAttachmentsResponse {
  transaction_id: string;
  entity_id: string;
  has_attachments: boolean;
  attachments: AttachmentMetadata[];
  total_count: number;
  failed_count?: number;
  failed_attachments?: AttachmentMetadata[];
  partial_success?: boolean;
  source: 'firestore' | 'xero' | 'firestore_cached';
}

export interface BatchAttachmentCheckResponse {
  results: Record<string, boolean>;
  checked_count: number;
  failed_batch_count: number;
}

class ComplianceService {
  private baseUrl = '/compliance/v2';  // Updated to use V2 sub-collection architecture
  
  // Request deduplication for vendor history
  private vendorHistoryRequests = new Map<string, Promise<import('../types/vendor-history.types').VendorHistoryResponse>>();
  
  // Rate limiting for security
  private requestCounts = new Map<string, { count: number; resetTime: number }>();
  private readonly MAX_REQUESTS_PER_MINUTE = 30;
  private readonly MAX_MAP_SIZE = 1000; // Prevent unbounded growth
  
  // Periodic cleanup timer
  private cleanupTimer: NodeJS.Timeout | null = null;

  /**
   * Get related items for a specific transaction by source_id
   * Searches across compliance issues, AML issues, and prepayment schedules
   */
  async getRelatedIssues(
    entityId: string,
    sourceId: string,
    options: RelatedIssuesOptions = {}
  ): Promise<RelatedIssuesResponse> {
    try {
      // Validate inputs
      if (!entityId || typeof entityId !== 'string' || !entityId.trim()) {
        throw new Error('Invalid entity ID provided');
      }

      if (!sourceId || typeof sourceId !== 'string' || !sourceId.trim()) {
        throw new Error('Invalid source ID provided');
      }

      console.log(`[ComplianceService] Fetching related items for source_id=${sourceId} in entity ${entityId}`);

      const queryParams = new URLSearchParams();
      queryParams.append('source_id', sourceId);

      if (options.exclude_current !== undefined) {
        queryParams.append('exclude_id', options.exclude_current.toString());
      }
      if (options.include_statuses?.length) {
        options.include_statuses.forEach(status => queryParams.append('include_statuses', status));
      }
      if (options.limit !== undefined) {
        queryParams.append('limit', options.limit.toString());
      }

      const url = `${this.baseUrl}/${entityId}/related-items?${queryParams.toString()}`;

      const response = await api.get<RelatedIssuesResponse>(url);

      console.log(`[ComplianceService] Successfully loaded ${response.related_issues?.length || 0} related items for source_id=${sourceId}`);

      return response;
    } catch (error: any) {
      console.error(`[ComplianceService] Error fetching related items for source_id=${sourceId}:`, error);

      if (error.response?.status === 404) {
        throw new Error('No related items found for this transaction.');
      } else if (error.response?.status === 403) {
        throw new Error('Access denied. Please check your permissions.');
      } else {
        const message = error.response?.data?.detail || 'Failed to fetch related items';
        throw new Error(message);
      }
    }
  }


  /**
   * Get detailed compliance issues for a specific entity
   */
  async getEntityIssues(entityId: string, params: Omit<ComplianceRequestParams, 'entity_id'> = {}): Promise<ComplianceResponse> {
    try {
      const queryParams = new URLSearchParams();

      // Deep link - fetch single issue by hash (fast path)
      if (params.issue_hash) {
        queryParams.append('issue_hash', params.issue_hash);
      }

      if (params.issue_types?.length) {
        params.issue_types.forEach(type => queryParams.append('issue_types', type));
      }
      if (params.severity?.length) {
        params.severity.forEach(sev => queryParams.append('severity', sev));
      }
      if (params.status?.length) {
        params.status.forEach(stat => queryParams.append('status', stat));
      }
      if (params.date_from) queryParams.append('date_from', params.date_from);
      if (params.date_to) queryParams.append('date_to', params.date_to);
      if (params.entity_ids?.length) {
        params.entity_ids.forEach(id => queryParams.append('entity_ids', id));
      }
      if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
      if (params.offset !== undefined) queryParams.append('offset', params.offset.toString());

      const url = `${this.baseUrl}/${entityId}/issues${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

      console.log(`[ComplianceService] Fetching entity issues for ${entityId} with params:`, params);

      const rawResponse = await api.get<ComplianceResponse>(url, {
        signal: params.signal
      });
      
      console.log(`[ComplianceService] Raw API response for entity ${entityId}:`, rawResponse);
      
      // Validate the response before processing
      const validatedResponse = validateComplianceResponse(rawResponse);
      
      if (!validatedResponse) {
        console.error(`[ComplianceService] Invalid response structure for entity ${entityId}:`, rawResponse);
        
        // Return fallback response instead of throwing to prevent UI crashes
        console.warn(`[ComplianceService] Using fallback response for entity ${entityId}`);
        return createFallbackComplianceResponse(entityId);
      }
      
      const originalActiveCount = rawResponse.active_issues?.length || 0;
      const originalDismissedCount = rawResponse.dismissed_issues?.length || 0;
      const validActiveCount = validatedResponse.active_issues.length;
      const validDismissedCount = validatedResponse.dismissed_issues.length;
      
      if (validActiveCount < originalActiveCount || validDismissedCount < originalDismissedCount) {
        console.warn(
          `[ComplianceService] Filtered invalid issues for entity ${entityId}: ` +
          `${originalActiveCount - validActiveCount} active, ` +
          `${originalDismissedCount - validDismissedCount} dismissed`
        );
      }
      
      console.log(
        `[ComplianceService] Successfully loaded ${validActiveCount} active and ` +
        `${validDismissedCount} dismissed issues for entity ${entityId}`
      );
      
      return validatedResponse;
    } catch (error: any) {
      // Handle abort/cancel errors - re-throw for caller handling
      if (isRequestCancelled(error)) {
        console.log(`[ComplianceService] Entity issues request aborted for entity ${entityId}`);
        throw error; // Re-throw so caller can handle it
      }

      console.error(`[ComplianceService] Error fetching entity issues for ${entityId}:`, error);

      // Handle different types of errors
      if (error.name === 'TypeError' || error.message?.includes('JSON')) {
        throw new Error('Server returned invalid data format. Please contact support.');
      } else if (error.response?.status === 404) {
        throw new Error('Entity not found or compliance data not available.');
      } else if (error.response?.status === 403) {
        throw new Error('Access denied. Please check your permissions.');
      } else {
        const message = error.response?.data?.detail || 'Failed to fetch entity compliance issues';
        throw new Error(message);
      }
    }
  }

  /**
   * Get all compliance issues for a client (load all per client pattern)
   * Note: This method may not be available in the current backend API
   * Consider using getEntityIssues for individual entities instead
   */
  async getClientIssues(clientId: string, params: Omit<ComplianceRequestParams, 'client_id'> = {}): Promise<LegacyComplianceResponse> {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.issue_types?.length) {
        params.issue_types.forEach(type => queryParams.append('issue_types', type));
      }
      if (params.severity?.length) {
        params.severity.forEach(sev => queryParams.append('severity', sev));
      }
      if (params.status?.length) {
        params.status.forEach(stat => queryParams.append('status', stat));
      }
      if (params.date_from) queryParams.append('date_from', params.date_from);
      if (params.date_to) queryParams.append('date_to', params.date_to);
      if (params.entity_ids?.length) {
        params.entity_ids.forEach(id => queryParams.append('entity_ids', id));
      }
      if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
      if (params.offset !== undefined) queryParams.append('offset', params.offset.toString());

      const url = `${this.baseUrl}/client/${clientId}/issues${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      console.log(`[ComplianceService] Fetching client issues for ${clientId} with params:`, params);
      
      const rawResponse = await api.get<any>(url);
      
      console.log(`[ComplianceService] Raw API response for client ${clientId}:`, rawResponse);
      
      // Validate the legacy response format
      const validatedResponse = validateLegacyComplianceResponse(rawResponse);
      
      if (!validatedResponse) {
        console.error(`[ComplianceService] Invalid legacy response structure for client ${clientId}:`, rawResponse);
        
        // Return fallback response instead of throwing to prevent UI crashes
        console.warn(`[ComplianceService] Using fallback response for client ${clientId}`);
        return createFallbackLegacyComplianceResponse();
      }
      
      const originalIssueCount = rawResponse.issues?.length || 0;
      const validIssueCount = validatedResponse.issues.length;
      
      if (validIssueCount < originalIssueCount) {
        console.warn(
          `[ComplianceService] Filtered ${originalIssueCount - validIssueCount} invalid issues ` +
          `for client ${clientId} (${validIssueCount}/${originalIssueCount} valid)`
        );
      }
      
      console.log(
        `[ComplianceService] Successfully loaded ${validIssueCount} issues ` +
        `for client ${clientId} (${validatedResponse.total_issues} total, ${validatedResponse.filtered_issues} filtered)`
      );
      
      return validatedResponse;
    } catch (error: any) {
      console.error(`[ComplianceService] Error fetching client issues for ${clientId}:`, error);
      
      // Handle different types of errors
      if (error.name === 'TypeError' || error.message?.includes('JSON')) {
        throw new Error('Server returned invalid data format. Please contact support.');
      } else if (error.response?.status === 404) {
        throw new Error('Client not found or compliance data not available.');
      } else if (error.response?.status === 403) {
        throw new Error('Access denied. Please check your permissions.');
      } else {
        const message = error.response?.data?.detail || 'Failed to fetch client compliance issues';
        throw new Error(message);
      }
    }
  }

  /**
   * Get compliance status for an entity
   */
  async getComplianceStatus(entityId: string): Promise<any> {
    try {
      const response = await api.get<any>(`${this.baseUrl}/${entityId}/status`);
      return response;
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to fetch compliance status';
      throw new Error(message);
    }
  }

  /**
   * Trigger compliance refresh for an entity
   * @param entityId - The entity ID to refresh compliance for
   * @param issueTypes - Optional array of issue types to refresh (e.g., ['F', 'G', 'H']). If not provided, all types will be refreshed.
   */
  async refreshCompliance(entityId: string, issueTypes?: string[]): Promise<any> {
    try {
      // Build query params if issue types are provided
      const params = issueTypes && issueTypes.length > 0
        ? { issue_types: issueTypes.join(',') }
        : {};

      const response = await api.post<any>(`${this.baseUrl}/${entityId}/refresh`, {}, {
        params,
        timeout: 300000 // 5 minutes for compliance refresh (can be slow for large entities)
      });
      return response;
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to trigger compliance refresh';
      throw new Error(message);
    }
  }

  // Legacy methods removed - focus on dismiss/undismiss operations only
  // Resolve functionality not implemented in current backend API

  /**
   * Dismiss a compliance issue using hash-based endpoint
   */
  async dismissIssue(entityId: string, issueHash: string, reason?: string): Promise<void> {
    try {
      // Validate inputs
      if (!entityId || typeof entityId !== 'string' || !entityId.trim()) {
        throw new Error('Invalid entity ID provided');
      }
      
      if (!isValidIssueHash(issueHash)) {
        throw new Error('Invalid issue hash format. Hash must be a 32-character hex string.');
      }
      
      if (reason !== undefined && (typeof reason !== 'string' || reason.length > 2000)) {
        throw new Error('Dismissal reason must be a string with maximum 2000 characters.');
      }
      
      console.log(`[ComplianceService] Dismissing issue ${issueHash} for entity ${entityId}`);
      
      const payload = {
        issue_hash: issueHash,
        ...(reason && { reason: reason.trim() })
      };
      
      const response = await api.post<any>(`${this.baseUrl}/${entityId}/issues/dismiss`, payload);
      
      console.log(`[ComplianceService] Successfully dismissed issue ${issueHash}`);
      return response;
    } catch (error: any) {
      console.error(`[ComplianceService] Error dismissing issue ${issueHash}:`, error);
      
      if (error.response?.status === 404) {
        throw new Error('Issue not found or already processed.');
      } else if (error.response?.status === 400) {
        throw new Error('Invalid request. Please check the issue details and try again.');
      } else {
        const message = error.response?.data?.detail || 'Failed to dismiss compliance issue';
        throw new Error(message);
      }
    }
  }

  /**
   * Undismiss a compliance issue using hash-based endpoint
   */
  async undismissIssue(entityId: string, issueHash: string): Promise<void> {
    try {
      // Validate inputs
      if (!entityId || typeof entityId !== 'string' || !entityId.trim()) {
        throw new Error('Invalid entity ID provided');
      }
      
      if (!isValidIssueHash(issueHash)) {
        throw new Error('Invalid issue hash format. Hash must be a 32-character hex string.');
      }
      
      console.log(`[ComplianceService] Undismissing issue ${issueHash} for entity ${entityId}`);
      
      const response = await api.post<any>(`${this.baseUrl}/${entityId}/issues/undismiss`, {
        issue_hash: issueHash
      });
      
      console.log(`[ComplianceService] Successfully undismissed issue ${issueHash}`);
      return response;
    } catch (error: any) {
      console.error(`[ComplianceService] Error undismissing issue ${issueHash}:`, error);
      
      if (error.response?.status === 404) {
        throw new Error('Issue not found or not currently dismissed.');
      } else if (error.response?.status === 400) {
        throw new Error('Invalid request. Please check the issue details and try again.');
      } else {
        const message = error.response?.data?.detail || 'Failed to undismiss compliance issue';
        throw new Error(message);
      }
    }
  }

  // Bulk operations removed for now - focus on individual issue operations
  // Can be re-implemented later if backend supports bulk hash-based operations

  // Smart methods removed - using hash-based endpoints exclusively now

  /**
   * Helper to get issue type display name
   */
  getIssueTypeDisplayName(type: string | undefined): string {
    if (!type || typeof type !== 'string') {
      return '';
    }
    
    const typeMap: Record<string, string> = {
      'A1': 'VAT Mismatch Case 1',
      'A2': 'VAT Mismatch Case 2',
      'A3': 'VAT Mismatch Case 3',
      'A4': 'VAT Mismatch Case 4',
      'B1': 'Mixed VAT Across Contact',
      'C1': 'Duplicate Bills',
      'D1': 'Spend vs Bill Duplicates',
      'E1': 'Mixed GL by Supplier',
      'J1': 'Consistent GL Coding'
    };
    return typeMap[type] || type;
  }

  /**
   * Helper to get issue type description
   */
  getIssueTypeDescription(type: string): string {
    const descMap: Record<string, string> = {
      'A1': 'Transaction contains line items with different VAT rates',
      'A2': 'VAT configuration conflicts with account setup',
      'A3': 'VAT rate type mismatches within transactions',
      'A4': 'Complex VAT configuration inconsistencies',
      'B1': 'Contact uses different VAT rates across multiple transactions',
      'C1': 'Potential duplicate bill detected (same supplier, date, amount)',
      'D1': 'Possible duplicate between spend money and bill entries',
      'E1': 'Supplier uses different GL accounts across transactions',
      'J1': 'GL coding consistency analysis'
    };
    return descMap[type] || 'Compliance issue';
  }

  /**
   * Helper to get severity color class
   */
  getSeverityColorClass(severity: string): string {
    const colorMap: Record<string, string> = {
      'HIGH': 'text-red-700 bg-red-50 border-red-200',
      'MEDIUM': 'text-yellow-700 bg-yellow-50 border-yellow-200',
      'LOW': 'text-blue-700 bg-blue-50 border-blue-200'
    };
    return colorMap[severity] || 'text-gray-700 bg-gray-50 border-gray-200';
  }

  /**
   * Helper to format transaction date
   */
  formatTransactionDate(dateString?: string): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  }

  /**
   * Helper to format detection count for hash-based issues
   */
  formatDetectionCount(detectionCount?: number): string {
    if (!detectionCount || detectionCount <= 1) return '';
    return `Detected ${detectionCount}x`;
  }

  /**
   * Helper to format first detected date
   */
  formatFirstDetectedDate(dateString?: string): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return `First seen: ${date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit', 
        year: 'numeric'
      })}`;
    } catch {
      return dateString;
    }
  }

  /**
   * Helper to validate issue hash format (64 characters SHA-256)
   * @deprecated Use isValidIssueHash from validation utilities instead
   */
  validateIssueHash(issueHash: string): boolean {
    return isValidIssueHash(issueHash);
  }

  /**
   * Type guard for A1 compliance issues (Mixed VAT)
   */
  isA1Issue(issue: ComplianceIssue): issue is ComplianceIssue & { 
    issue_type: 'A1'; 
    affected_line_items: LineItemDetail[];
  } {
    return issue.issue_type === 'A1' && !!issue.affected_line_items;
  }

  /**
   * Type guard for A2 compliance issues (VAT Configuration)
   * Note: A2 issues may not have line_index/line_description in API response
   * We'll detect based on issue_type only and handle data extraction later
   */
  isA2Issue(issue: ComplianceIssue): issue is ComplianceIssue & { 
    issue_type: 'A2'; 
  } {
    const result = issue.issue_type === 'A2';
    
    if (issue.issue_type === 'A2') {
      console.log('[ComplianceService.isA2Issue] A2 issue debug:', {
        issueHash: issue.issue_hash,
        issueType: issue.issue_type,
        lineIndex: issue.line_index,
        lineIndexType: typeof issue.line_index,
        lineDescription: issue.line_description,
        hasLineDescription: !!issue.line_description,
        metadata: issue.metadata,
        result,
        fullIssue: issue
      });
    }
    
    return result;
  }

  /**
   * Type guard for line item specific issues (A1/A2)
   */
  isLineItemSpecificIssue(issue: ComplianceIssue): boolean {
    const result = this.isA1Issue(issue) || this.isA2Issue(issue);
    console.log('[ComplianceService.isLineItemSpecificIssue]', {
      issueHash: issue.issue_hash,
      issueType: issue.issue_type,
      type: issue.type,
      title: issue.title,
      hasAffectedLineItems: !!issue.affected_line_items,
      affectedLineItemsLength: issue.affected_line_items?.length,
      lineIndex: issue.line_index,
      lineDescription: issue.line_description,
      isA1: this.isA1Issue(issue),
      isA2: this.isA2Issue(issue),
      result
    });
    return result;
  }

  /**
   * Get line item details from compliance issue by fetching the source transaction
   */
  async getLineItemDetailsFromTransaction(issue: ComplianceIssue): Promise<LineItemDetail[]> {
    console.log('[ComplianceService.getLineItemDetailsFromTransaction] Fetching transaction for issue:', {
      issueHash: issue.issue_hash,
      issueType: issue.issue_type,
      sourceId: issue.source_id,
      sourceTransactionReference: issue.source_transaction_reference,
      entityId: issue.entity_id,
      metadata: issue.metadata
    });

    if (!issue.source_id || !issue.entity_id) {
      console.error('Missing source_id or entity_id for transaction fetch');
      throw new Error('Cannot fetch transaction: missing source_id or entity_id');
    }

    try {
      // Try multiple possible API endpoints for transaction data
      let transaction: any = null;
      let endpoint: string = '';
      
      const possibleEndpoints = [
        `/transactions/${issue.source_id}`,
        `/entities/${issue.entity_id}/transactions/${issue.source_id}`,
        `/compliance/transactions/${issue.source_id}`,
        `/bills/${issue.source_id}`,  // For ACCPAY transactions
        `/invoices/${issue.source_id}` // For ACCREC transactions
      ];
      
      for (const testEndpoint of possibleEndpoints) {
        try {
          console.log(`[ComplianceService] Trying endpoint: ${testEndpoint}`);
          const response = await api.get<any>(testEndpoint);
          transaction = response.data || response;
          endpoint = testEndpoint;
          break;
        } catch (err: any) {
          if (err.response?.status !== 404) {
            // If it's not a 404, it's a real error
            throw err;
          }
          // 404 means endpoint doesn't exist, try next one
          console.log(`[ComplianceService] Endpoint ${testEndpoint} returned 404, trying next...`);
        }
      }
      
      if (!transaction) {
        throw new Error('Transaction not found at any known API endpoint');
      }
      
      console.log(`[ComplianceService.getLineItemDetailsFromTransaction] Found transaction at ${endpoint}:`, transaction);

      if (!transaction.line_items || !Array.isArray(transaction.line_items)) {
        console.error('Transaction missing line_items array');
        throw new Error('Transaction does not contain line items');
      }

      // Convert transaction line items to our format
      const lineItems: LineItemDetail[] = transaction.line_items.map((item: any, index: number) => {
        // Ensure line_index is always a number (required field), use array index as fallback
        const lineIndex = typeof item.line_index === 'number' ? item.line_index : index;
        
        const lineItem: LineItemDetail = {
          line_item_id: String(item.line_item_id || item.LineItemID || item.id || `line_${index}`),
          line_index: lineIndex, // Always a number, never undefined
          tax_type: item.tax_type || item.vat_type || item.TaxType || 'STANDARD',
          line_amount: parseFloat(item.line_amount || item.amount || item.LineAmount || 0),
          description: item.description || item.line_description || item.Description || `Line ${index + 1}`,
          account_code: item.account_code || item.account || item.AccountCode || '',
          // Enhanced fields from backend (Chart of Accounts and Tax)
          account_name: item.account_name || null,
          formatted_account: item.formatted_account || null,
          tax_amount: typeof item.tax_amount === 'number' ? item.tax_amount : null,
          formatted_tax: item.formatted_tax || null
        };
        return lineItem;
      });

      console.log('[ComplianceService.getLineItemDetailsFromTransaction] Converted line items:', lineItems);
      return lineItems;

    } catch (error: any) {
      console.error('Error fetching transaction for line items:', error);
      throw new Error(`Failed to fetch transaction line items: ${error.message}`);
    }
  }

  /**
   * Get line item details from compliance issue (legacy method - keeping for compatibility)
   * @deprecated Use getLineItemDetailsFromTransaction for A1/A2 issues
   */
  getLineItemDetails(issue: ComplianceIssue): LineItemDetail[] | null {
    // For A1 issues that already have line items embedded
    if (this.isA1Issue(issue) && issue.affected_line_items) {
      return issue.affected_line_items;
    }
    
    // For A2 issues, we need to fetch from transaction
    // This method is synchronous so can't fetch - use getLineItemDetailsFromTransaction instead
    return null;
  }

  /**
   * Get display text for line item issues
   */
  getLineItemDisplayText(issue: ComplianceIssue): string | null {
    if (this.isA1Issue(issue)) {
      const count = issue.affected_line_items.length;
      return `${count} line item${count > 1 ? 's' : ''} affected`;
    }
    if (this.isA2Issue(issue)) {
      const lineIndex = typeof issue.line_index === 'number' ? issue.line_index : 0;
      return `Line ${lineIndex + 1}: ${issue.line_description}`;
    }
    return null;
  }

  /**
   * Helper to normalize severity values from backend to frontend format
   */
  private normalizeSeverity(severity: string): 'HIGH' | 'MEDIUM' | 'LOW' {
    const normalized = severity.toUpperCase();
    if (normalized === 'HIGH' || normalized === 'MEDIUM' || normalized === 'LOW') {
      return normalized as 'HIGH' | 'MEDIUM' | 'LOW';
    }
    return 'MEDIUM'; // Default fallback
  }

  /**
   * Helper to get all issues (active + dismissed) from ComplianceResponse
   */
  getAllIssues(response: ComplianceResponse): ComplianceIssue[] {
    const allIssues: ComplianceIssue[] = [];
    
    // Add active issues with status: 'active'
    response.active_issues.forEach(issue => {
      allIssues.push({
        ...issue,
        status: 'review' as const,
        severity: this.normalizeSeverity(issue.severity)
      });
    });
    
    // Add dismissed issues with status: 'dismissed'
    response.dismissed_issues.forEach(issue => {
      allIssues.push({
        ...issue,
        status: 'ok' as const,
        severity: this.normalizeSeverity(issue.severity || 'MEDIUM')
      });
    });
    
    return allIssues;
  }

  /**
   * Helper to get dismissal information
   */
  getDismissalInfo(issue: ComplianceIssue): { reason?: string; date?: string; by?: string } | null {
    if (issue.status !== 'ok') return null;
    
    return {
      reason: issue.last_message || 'No reason provided',
      date: issue.last_status_change ? this.formatTransactionDate(issue.last_status_change) : undefined,
      by: issue.last_changed_by
    };
  }

  /**
   * Helper to check if an issue can be dismissed (is currently in review/active state)
   */
  canDismissIssue(issue: ComplianceIssue): boolean {
    return (issue.status === 'review' || issue.status === 'fix') && this.validateIssueHash(issue.issue_hash);
  }

  /**
   * Helper to check if an issue can be undismissed (is currently dismissed/ok)
   */
  canUndismissIssue(issue: ComplianceIssue): boolean {
    return (issue.status === 'ok') && this.validateIssueHash(issue.issue_hash);
  }

  /**
   * Helper to check if an issue can be resolved (marked as fix)
   */
  canResolveIssue(issue: ComplianceIssue): boolean {
    return (issue.status === 'review') && this.validateIssueHash(issue.issue_hash);
  }

  /**
   * Helper to check if an issue can be moved to review status
   */
  canReviewIssue(issue: ComplianceIssue): boolean {
    return issue.status === 'fix' && this.validateIssueHash(issue.issue_hash);
  }

  /**
   * Helper to check if an issue is hash-based (new format vs legacy)
   */
  isHashBasedIssue(issue: ComplianceIssue): boolean {
    return !!issue.issue_hash && this.validateIssueHash(issue.issue_hash);
  }

  /**
   * Resolve a single issue (mark as fix)
   */
  async resolveIssue(entityId: string, issueHash: string, notes?: string): Promise<void> {
    try {
      // Validate inputs
      if (!entityId || typeof entityId !== 'string' || !entityId.trim()) {
        throw new Error('Invalid entity ID provided');
      }
      
      if (!isValidIssueHash(issueHash)) {
        throw new Error('Invalid issue hash format. Hash must be a 32-character hex string.');
      }
      
      if (notes !== undefined && (typeof notes !== 'string' || notes.length > 2000)) {
        throw new Error('Resolution notes must be a string with maximum 2000 characters.');
      }
      
      console.log(`[ComplianceService] Resolving issue ${issueHash} for entity ${entityId}`);
      
      const payload = {
        issue_hash: issueHash,
        ...(notes && { notes: notes.trim() })
      };
      
      const response = await api.post<any>(`${this.baseUrl}/${entityId}/issues/resolve`, payload);
      
      console.log(`[ComplianceService] Successfully resolved issue ${issueHash}`);
      return response;
    } catch (error: any) {
      console.error(`[ComplianceService] Error resolving issue ${issueHash}:`, error);

      if (error.response?.status === 404) {
        throw new Error('Issue not found or may have been removed. Please refresh and try again.');
      } else if (error.response?.status === 409) {
        throw new Error('Issue has already been resolved. Please refresh to see the current status.');
      } else if (error.response?.status === 400) {
        throw new Error('Invalid request. Please check the issue details and try again.');
      } else {
        const message = error.response?.data?.detail || 'Failed to resolve compliance issue';
        throw new Error(message);
      }
    }
  }

  /**
   * Move issue back to review status
   */
  async reviewIssue(entityId: string, issueHash: string, notes?: string): Promise<void> {
    try {
      // Validate inputs
      if (!entityId || typeof entityId !== 'string' || !entityId.trim()) {
        throw new Error('Invalid entity ID provided');
      }
      
      if (!isValidIssueHash(issueHash)) {
        throw new Error('Invalid issue hash format. Hash must be a 32-character hex string.');
      }
      
      if (notes !== undefined && (typeof notes !== 'string' || notes.length > 2000)) {
        throw new Error('Review notes must be a string with maximum 2000 characters.');
      }
      
      console.log(`[ComplianceService] Moving issue ${issueHash} to review for entity ${entityId}`);
      
      const payload = {
        issue_hash: issueHash,
        ...(notes && { notes: notes.trim() })
      };
      
      const response = await api.post<any>(`${this.baseUrl}/${entityId}/issues/review`, payload);
      
      console.log(`[ComplianceService] Successfully moved issue ${issueHash} to review`);
      return response;
    } catch (error: any) {
      console.error(`[ComplianceService] Error moving issue ${issueHash} to review:`, error);

      if (error.response?.status === 404) {
        throw new Error('Issue not found or may have been removed. Please refresh and try again.');
      } else if (error.response?.status === 409) {
        throw new Error('Issue status has changed. Please refresh to see the current status.');
      } else if (error.response?.status === 400) {
        throw new Error('Invalid request. Please check the issue details and try again.');
      } else {
        const message = error.response?.data?.detail || 'Failed to move compliance issue to review';
        throw new Error(message);
      }
    }
  }

  /**
   * Bulk resolve multiple issues with progress tracking
   */
  async bulkResolveIssues(
    entityId: string,
    issueHashes: string[],
    notes?: string,
    onProgress?: (completed: number, total: number, errors: any[]) => void
  ): Promise<EnhancedBulkOperationResult> {
    return this.performBulkOperationWithRetry(
      entityId,
      issueHashes,
      'resolve',
      notes,
      onProgress
    );
  }

  /**
   * Bulk dismiss multiple issues with progress tracking
   */
  async bulkDismissIssues(
    entityId: string,
    issueHashes: string[],
    reason?: string,
    onProgress?: (completed: number, total: number, errors: any[]) => void
  ): Promise<EnhancedBulkOperationResult> {
    return this.performBulkOperationWithRetry(
      entityId,
      issueHashes,
      'dismiss',
      reason,
      onProgress
    );
  }

  /**
   * Bulk resolve issues with comment creation
   */
  async bulkResolveIssuesWithComment(
    entityId: string,
    issueHashes: string[],
    userInput?: string,
    mentions?: string[],
    onProgress?: (completed: number, total: number, errors: any[]) => void
  ): Promise<EnhancedBulkOperationResult> {
    return this.performBulkOperationWithCommentAndRetry(
      entityId,
      issueHashes,
      'resolve',
      userInput,
      mentions,
      onProgress
    );
  }

  /**
   * Bulk dismiss issues with comment creation
   */
  async bulkDismissIssuesWithComment(
    entityId: string,
    issueHashes: string[],
    userInput?: string,
    mentions?: string[],
    onProgress?: (completed: number, total: number, errors: any[]) => void
  ): Promise<EnhancedBulkOperationResult> {
    return this.performBulkOperationWithCommentAndRetry(
      entityId,
      issueHashes,
      'dismiss',
      userInput,
      mentions,
      onProgress
    );
  }

  // ========== ATOMIC OPERATIONS WITH COMMENTS ==========
  
  /**
   * Resolve issue and optionally create a comment in a single operation
   * Updated for unified input system - userInput contains rich HTML for comments and gets converted to plain text for notes
   */
  async resolveIssueWithComment(
    entityId: string, 
    issueHash: string, 
    userInput?: string,
    mentions?: string[]
  ): Promise<void> {
    try {
      console.log(`[ComplianceService] Resolving issue ${issueHash} with unified input for entity ${entityId}`);
      
      let plainTextNotes: string | undefined;
      
      // Process unified input if provided
      if (userInput && userInput.trim()) {
        // Import message processing utilities
        const { processUnifiedMessage } = await import('../utils/message-processing');
        const processed = processUnifiedMessage(userInput);
        plainTextNotes = processed.plainText || undefined;
      }
      
      // Step 1: Resolve the issue first (API expects plain text notes)
      await this.resolveIssue(entityId, issueHash, plainTextNotes);
      
      // Step 2: Create comment if provided (uses rich HTML)
      if (userInput && userInput.trim()) {
        await this.createStatusChangeComment(issueHash, 'resolve', userInput.trim(), mentions);
      }
      
      console.log(`[ComplianceService] Successfully resolved issue ${issueHash} with unified input`);
    } catch (error: any) {
      console.error(`[ComplianceService] Error in resolveIssueWithComment for ${issueHash}:`, error);
      
      // For MVP: If status change succeeds but comment fails, that's acceptable
      // The status change is more important than the comment
      if (error.message?.includes('Failed to create comment')) {
        console.warn(`[ComplianceService] Issue resolved but comment creation failed: ${error.message}`);
        // Don't throw - status change succeeded which is the primary operation
        return;
      }
      
      // Re-throw for status change failures
      throw error;
    }
  }

  /**
   * Move issue to review and optionally create a comment in a single operation
   * Updated for unified input system - userInput contains rich HTML for comments and gets converted to plain text for notes
   */
  async reviewIssueWithComment(
    entityId: string, 
    issueHash: string, 
    userInput?: string,
    mentions?: string[]
  ): Promise<void> {
    try {
      console.log(`[ComplianceService] Moving issue ${issueHash} to review with unified input for entity ${entityId}`);
      
      let plainTextNotes: string | undefined;
      
      // Process unified input if provided
      if (userInput && userInput.trim()) {
        // Import message processing utilities
        const { processUnifiedMessage } = await import('../utils/message-processing');
        const processed = processUnifiedMessage(userInput);
        plainTextNotes = processed.plainText || undefined;
      }
      
      // Step 1: Move to review first (API expects plain text notes)
      await this.reviewIssue(entityId, issueHash, plainTextNotes);
      
      // Step 2: Create comment if provided (uses rich HTML)
      if (userInput && userInput.trim()) {
        await this.createStatusChangeComment(issueHash, 'review', userInput.trim(), mentions);
      }
      
      console.log(`[ComplianceService] Successfully moved issue ${issueHash} to review with unified input`);
    } catch (error: any) {
      console.error(`[ComplianceService] Error in reviewIssueWithComment for ${issueHash}:`, error);
      
      // For MVP: If status change succeeds but comment fails, that's acceptable
      if (error.message?.includes('Failed to create comment')) {
        console.warn(`[ComplianceService] Issue moved to review but comment creation failed: ${error.message}`);
        return;
      }
      
      throw error;
    }
  }

  /**
   * Dismiss issue and optionally create a comment in a single operation
   * Updated for unified input system - userInput contains rich HTML for comments and gets converted to plain text for notes
   */
  async dismissIssueWithComment(
    entityId: string, 
    issueHash: string, 
    userInput?: string,
    mentions?: string[]
  ): Promise<void> {
    try {
      console.log(`[ComplianceService] Dismissing issue ${issueHash} with unified input for entity ${entityId}`);
      
      let plainTextNotes: string | undefined;
      
      // Process unified input if provided
      if (userInput && userInput.trim()) {
        // Import message processing utilities
        const { processUnifiedMessage } = await import('../utils/message-processing');
        const processed = processUnifiedMessage(userInput);
        plainTextNotes = processed.plainText || undefined;
      }
      
      // Step 1: Dismiss the issue first (API expects plain text notes)
      await this.dismissIssue(entityId, issueHash, plainTextNotes);
      
      // Step 2: Create comment if provided (uses rich HTML)
      if (userInput && userInput.trim()) {
        await this.createStatusChangeComment(issueHash, 'dismiss', userInput.trim(), mentions);
      }
      
      console.log(`[ComplianceService] Successfully dismissed issue ${issueHash} with unified input`);
    } catch (error: any) {
      console.error(`[ComplianceService] Error in dismissIssueWithComment for ${issueHash}:`, error);
      
      // For MVP: If status change succeeds but comment fails, that's acceptable
      if (error.message?.includes('Failed to create comment')) {
        console.warn(`[ComplianceService] Issue dismissed but comment creation failed: ${error.message}`);
        return;
      }
      
      throw error;
    }
  }

  /**
   * Undismiss issue and optionally create a comment in a single operation
   * Updated for unified input system - userInput contains rich HTML for comments and gets converted to plain text for notes
   */
  async undismissIssueWithComment(
    entityId: string, 
    issueHash: string, 
    userInput?: string,
    mentions?: string[]
  ): Promise<void> {
    try {
      console.log(`[ComplianceService] Undismissing issue ${issueHash} with unified input for entity ${entityId}`);
      
      // Note: undismissIssue doesn't take notes parameter, only the other methods do
      // Step 1: Undismiss the issue first
      await this.undismissIssue(entityId, issueHash);
      
      // Step 2: Create comment if provided (uses rich HTML)
      if (userInput && userInput.trim()) {
        await this.createStatusChangeComment(issueHash, 'undismiss', userInput.trim(), mentions);
      }
      
      console.log(`[ComplianceService] Successfully undismissed issue ${issueHash} with unified input`);
    } catch (error: any) {
      console.error(`[ComplianceService] Error in undismissIssueWithComment for ${issueHash}:`, error);
      
      // If comment creation fails after successful status change, log but don't throw
      if (error.message?.includes('comment') && !error.message?.includes('undismiss')) {
        console.warn(`[ComplianceService] Issue undismissed but comment creation failed: ${error.message}`);
        throw new Error(`Issue undismissed successfully, but comment creation failed: ${error.message}`);
      }
      
      throw error;
    }
  }

  /**
   * Private helper to create status change comments
   */
  private async createStatusChangeComment(
    issueHash: string,
    action: 'resolve' | 'review' | 'dismiss' | 'undismiss',
    comment: string,
    mentions?: string[]
  ): Promise<void> {
    try {
      // Import the comments service dynamically to avoid circular dependencies
      const { CommentsService, ParentType } = await import('./comments.service');
      const { generateActionComment } = await import('../utils/compliance-comment-formatting');
      
      // SECURITY: Sanitize HTML before storing in comments
      // For MVP, we strip all HTML tags to prevent XSS
      // TODO: Replace with DOMPurify for rich text support
      const { stripHtmlAndDecode } = await import('../utils/message-processing');
      
      const safeComment = stripHtmlAndDecode(comment).trim();
      if (!safeComment) {
        console.warn('[ComplianceService] Comment was empty after HTML stripping');
        return;
      }
      
      // We use the issue hash as the parent_id for compliance issue comments
      const commentData = {
        parent_type: ParentType.COMPLIANCE_ISSUE,
        parent_id: issueHash,
        text: safeComment,  // Store plain text for MVP security
        mention_uids: mentions || []
      };
      
      await CommentsService.createComment(commentData);
      
      console.log(`[ComplianceService] Successfully created status change comment for issue ${issueHash}`);
    } catch (error: any) {
      console.error(`[ComplianceService] Error creating status change comment:`, error);
      throw new Error(`Failed to create comment: ${error.message}`);
    }
  }

  // ========== VENDOR TRANSACTION HISTORY METHODS ==========

  /**
   * Get vendor transaction history with enhanced line items and account resolution
   */
  async getVendorHistory(
    entityId: string,
    vendorName: string,
    filters?: import('../types/vendor-history.types').VendorHistoryFilters,
    clientId?: string,
    contactId?: string,
    signal?: AbortSignal
  ): Promise<import('../types/vendor-history.types').VendorHistoryResponse> {

    // Input validation and sanitization
    if (!entityId || typeof entityId !== 'string' || !entityId.trim()) {
      throw new Error('Invalid entity ID provided');
    }

    // Require either contactId or vendorName
    if ((!contactId || typeof contactId !== 'string' || !contactId.trim()) &&
        (!vendorName || typeof vendorName !== 'string' || !vendorName.trim())) {
      throw new Error('Either contact ID or vendor name must be provided');
    }

    // Sanitize vendor name for security (XSS prevention) if provided
    const sanitizedVendorName = vendorName ? this.sanitizeVendorName(vendorName) : '';

    // Rate limiting check - use contact_id if available, fallback to vendor name
    const rateLimitKey = `${entityId}_${contactId || sanitizedVendorName}`;
    this.checkRateLimit(rateLimitKey);

    // Get client ID if not provided
    if (!clientId) {
      // Try to get from navigation store if available
      try {
        const { useNavigationStore } = await import('../store/navigation.store');
        clientId = useNavigationStore.getState().selectedClientId;

        if (!clientId) {
          throw new Error('No client ID provided and none selected in navigation store');
        }
      } catch (error) {
        throw new Error('Client ID is required for vendor history lookup');
      }
    }

    // Generate cache key for use throughout method - prefer contact_id for cache key
    const cacheKey = this.generateVendorHistoryCacheKey(entityId, contactId || sanitizedVendorName, filters);

    try {

      // Check cache first using SecureStorage
      const cachedData = this.getFromVendorHistoryCache(cacheKey);

      if (cachedData) {
        return cachedData;
      }

      // Request deduplication - prevent concurrent requests for same data
      if (this.vendorHistoryRequests.has(cacheKey)) {
        return this.vendorHistoryRequests.get(cacheKey)!;
      }

      // Create the API request promise and store it for deduplication
      const apiRequestPromise = (async () => {
        try {
          // Use the new dedicated vendor history endpoint
          const params = new URLSearchParams();
          params.append('entity_id', entityId);

          // Prefer contact_id over contact_name (more reliable and performant)
          if (contactId) {
            params.append('contact_id', contactId);
          } else {
            params.append('contact_name', sanitizedVendorName);
          }

          params.append('limit', (Math.min(filters?.limit || 50, 100)).toString());
          params.append('include_line_items', 'true');

          const transactions = await api.get(`/transactions/vendor-history?${params.toString()}`, { signal }) as any[];
          
          // The new endpoint returns transactions directly (not wrapped in a response object)
          let allTransactions = [];
          if (Array.isArray(transactions)) {
            allTransactions = transactions;
          } else {
            console.warn(`[ComplianceService] Unexpected API response format:`, transactions);
            allTransactions = [];
          }
          
          // Backend already filtered by contact_name, no need for client-side filtering
          const filteredTransactions = allTransactions;
          
          
          if (filteredTransactions.length === 0) {
            console.info(`[ComplianceService] No transactions found for vendor ${sanitizedVendorName}`);
          }

          // Backend already provides enhanced line items, no need for frontend processing
          console.log('🔥 SKIPPING FRONTEND CHART OF ACCOUNTS PROCESSING - backend already enhanced the data');
          
          // Create response object directly from backend-enhanced data
          const processedResponse = {
            vendor_name: sanitizedVendorName,
            entity_id: entityId,
            entity_name: "Entity Name", // TODO: Get from entity data if needed
            transactions: filteredTransactions.map((transaction: any) => ({
              transaction_id: transaction.id,
              document_number: transaction.document_number,
              document_reference: transaction.document_reference,
              type: transaction.type,
              transaction_type: transaction.transaction_type, // Original Xero transaction type for display
              date_issued: transaction.date_issued,
              date_due: transaction.date_due,
              total: transaction.total,
              amount_due: transaction.amount_due,
              amount_paid: transaction.amount_paid,
              currency: transaction.currency,
              status: transaction.status,
              source: transaction.source || "xero",
              source_id: transaction.source_id || transaction.id,
              has_attachments: transaction.has_attachments || false,
              has_amortization_schedules: transaction.has_amortization_schedules || false,
              line_items: transaction.line_items?.map((item: any) => ({
                id: String(item.id || 0),
                description: item.description || '',
                unit_amount: item.unit_amount || 0,
                quantity: item.quantity || 1,
                amount: item.line_amount || (item.unit_amount * item.quantity) || 0,
                account_code: item.account_code || '',
                account_name: item.account_name || null, // 🔥 ENHANCED FIELD
                formatted_account: item.formatted_account || null, // 🔥 ENHANCED FIELD
                tax_type: item.tax_type || '',
                tax_amount: item.tax_amount || null, // 🔥 ENHANCED FIELD
                formatted_tax: item.formatted_tax || null // 🔥 ENHANCED FIELD
              })) || []
            })),
            stats: {
              total_transactions: filteredTransactions.length,
              total_amount: filteredTransactions.reduce((sum: number, t: any) => sum + (t.total || 0), 0),
              average_transaction_amount: filteredTransactions.length > 0 ? 
                filteredTransactions.reduce((sum: number, t: any) => sum + (t.total || 0), 0) / filteredTransactions.length : 0,
              currency: filteredTransactions[0]?.currency || "GBP"
            },
            pagination: {
              current_page: 1,
              page_size: filteredTransactions.length,
              total_items: filteredTransactions.length,
              total_pages: 1
            }
          };

          // Cache the processed response
          this.cacheVendorHistoryData(cacheKey, processedResponse);

          console.log(
            `[ComplianceService] Successfully processed ${processedResponse.transactions.length} transactions ` +
            `for vendor ${sanitizedVendorName}`
          );

          return processedResponse;
        } finally {
          // Cleanup: remove from pending requests
          this.vendorHistoryRequests.delete(cacheKey);
        }
      })();

      // Store the promise for deduplication
      this.vendorHistoryRequests.set(cacheKey, apiRequestPromise);
      
      // Return the promise
      return apiRequestPromise;
    } catch (error: any) {
      // Cleanup: remove from pending requests on error
      this.vendorHistoryRequests.delete(cacheKey);
      
      console.error(`[ComplianceService] Error fetching vendor history:`, error);
      
      if (error.response?.status === 404) {
        throw new Error('Vendor transaction history not found or entity not accessible.');
      } else if (error.response?.status === 403) {
        throw new Error('Access denied. Please check your permissions.');
      } else {
        const message = error.response?.data?.detail || `Failed to fetch vendor history: ${error.message}`;
        throw new Error(message);
      }
    }
  }

  /**
   * Get chart of accounts with caching
   */
  private async getChartOfAccounts(entityId: string): Promise<any[]> {
    try {
      // Check cache first
      const cacheKey = `chart_of_accounts_${entityId}`;
      const cached = this.getFromAccountsCache(cacheKey);
      
      if (cached) {
        return cached;
      }

      // Fetch from API
      console.log(`[ComplianceService] Fetching chart of accounts for entity ${entityId}`);
      const response = await api.get<any>(`/entities/${entityId}/accounts`);
      
      const accounts = response.accounts || response || [];
      
      // Cache with 15-minute TTL
      this.cacheAccountsData(cacheKey, accounts);
      
      return accounts;
    } catch (error: any) {
      console.error(`[ComplianceService] Error fetching chart of accounts:`, error);
      // Return empty array to allow processing to continue without account names
      return [];
    }
  }

  /**
   * Process and enhance vendor history data
   */
  private async processVendorHistoryData(
    apiResponse: any,
    entityId: string,
    vendorName: string,
    chartOfAccounts: any[]
  ): Promise<import('../types/vendor-history.types').VendorHistoryResponse> {
    const transactions = apiResponse.transactions || [];
    
    // Create account lookup map for performance
    const accountLookup = new Map();
    chartOfAccounts.forEach((account: any) => {
      if (account.account_code) {
        accountLookup.set(account.account_code, {
          account_name: account.account_name || account.name,
          account_type: account.account_type || account.type,
        });
      }
    });

    // Process each transaction and enhance line items
    const processedTransactions: any[] = [];
    
    for (const transaction of transactions) {
      const enhancedLineItems = this.enhanceLineItems(transaction.line_items || [], accountLookup);
      
      processedTransactions.push({
        transaction_id: transaction.id || transaction.transaction_id,
        document_number: transaction.document_number || transaction.reference,
        document_reference: transaction.document_reference,
        type: transaction.type,
        date_issued: transaction.date_issued || transaction.date,
        date_due: transaction.date_due,
        total: transaction.total || 0,
        amount_due: transaction.amount_due,
        amount_paid: transaction.amount_paid,
        currency: transaction.currency || 'USD',
        status: transaction.status,
        source: transaction.source || 'xero',
        source_id: transaction.source_id,
        has_attachments: transaction.has_attachments || false,
        has_amortization_schedules: transaction.has_amortization_schedules || false,
        line_items: enhancedLineItems,
      });
    }

    // Generate statistics
    const stats = this.generateVendorHistoryStats(processedTransactions);

    // Build response matching expected interface
    return {
      vendor_name: vendorName,
      vendor_id: apiResponse.vendor_id,
      entity_id: entityId,
      entity_name: apiResponse.entity_name || '',
      transactions: processedTransactions,
      stats,
      pagination: {
        current_page: apiResponse.page || 1,
        page_size: apiResponse.limit || 50,
        total_items: apiResponse.total || processedTransactions.length,
        total_pages: apiResponse.total_pages || 1,
      },
    };
  }

  /**
   * Enhance line items with account names and formatted tax display
   */
  private enhanceLineItems(lineItems: any[], accountLookup: Map<string, any>): any[] {
    return lineItems.map((item: any) => {
      const accountInfo = accountLookup.get(item.account_code);
      const formattedAccount = this.formatAccountDisplay(item.account_code, accountInfo?.account_name);
      const formattedTax = this.formatTaxDisplay(item.tax_type, item.tax_amount);

      return {
        id: String(item.id || 0),
        description: item.description || '',
        unit_amount: item.unit_amount || 0,
        quantity: item.quantity || 1,
        amount: item.amount || (item.unit_amount || 0) * (item.quantity || 1),
        account_code: item.account_code,
        account_id: item.account_id,
        account_name: accountInfo?.account_name,
        tax_amount: item.tax_amount,
        tax_type: item.tax_type,
        formatted_tax: formattedTax,
        formatted_account: formattedAccount,
      };
    });
  }

  /**
   * Format account display: "6000 - Office Expenses" or just "6000"
   */
  private formatAccountDisplay(accountCode?: string, accountName?: string): string {
    if (!accountCode) return 'No Account';
    if (accountName) return `${accountCode} - ${accountName}`;
    return accountCode;
  }

  /**
   * Format tax display: "GST 10% - $45.00" or "No tax"
   */
  private formatTaxDisplay(taxType?: string, taxAmount?: number): string {
    if (!taxType || !taxAmount) return 'No tax';
    
    const formattedAmount = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(taxAmount);

    // Extract percentage from tax type if possible
    const percentageMatch = taxType.match(/(\d+)%?/);
    const percentage = percentageMatch ? `${percentageMatch[1]}%` : '';
    
    const displayType = taxType.replace(/\d+%?/, '').trim() || taxType;
    
    return percentage 
      ? `${displayType} ${percentage} - ${formattedAmount}`
      : `${displayType} - ${formattedAmount}`;
  }

  /**
   * Generate vendor transaction statistics
   */
  private generateVendorHistoryStats(transactions: any[]): any {
    if (transactions.length === 0) {
      return {
        total_transactions: 0,
        total_amount: 0,
        average_transaction_amount: 0,
        currency: 'USD',
        transaction_types: {},
        status_breakdown: {},
        common_line_items: [],
      };
    }

    const totalAmount = transactions.reduce((sum, t) => sum + (t.total || 0), 0);
    const currencies = transactions.map(t => t.currency).filter(Boolean);
    const mostCommonCurrency = this.getMostFrequent(currencies) || 'USD';

    const dates = transactions
      .map(t => t.date_issued)
      .filter(Boolean)
      .sort();

    const typeBreakdown: Record<string, number> = {};
    const statusBreakdown: Record<string, number> = {};

    transactions.forEach(transaction => {
      // Count transaction types
      if (transaction.type) {
        typeBreakdown[transaction.type] = (typeBreakdown[transaction.type] || 0) + 1;
      }

      // Count statuses
      if (transaction.status) {
        statusBreakdown[transaction.status] = (statusBreakdown[transaction.status] || 0) + 1;
      }
    });

    // Analyze common line items
    const lineItemDescriptions: Record<string, { count: number; totalAmount: number; accountCodes: Set<string> }> = {};
    
    transactions.forEach(transaction => {
      transaction.line_items?.forEach((item: any) => {
        if (item.description) {
          if (!lineItemDescriptions[item.description]) {
            lineItemDescriptions[item.description] = {
              count: 0,
              totalAmount: 0,
              accountCodes: new Set(),
            };
          }
          lineItemDescriptions[item.description].count++;
          lineItemDescriptions[item.description].totalAmount += item.amount || 0;
          if (item.account_code) {
            lineItemDescriptions[item.description].accountCodes.add(item.account_code);
          }
        }
      });
    });

    const commonLineItems = Object.entries(lineItemDescriptions)
      .map(([description, data]) => ({
        description,
        frequency: data.count,
        account_code: Array.from(data.accountCodes)[0], // Most common would need more logic
        average_amount: data.totalAmount / data.count,
        total_amount: data.totalAmount,
        currency: mostCommonCurrency,
      }))
      .sort((a, b) => b.frequency - a.frequency)
      .slice(0, 5); // Top 5 most common

    return {
      total_transactions: transactions.length,
      total_amount: totalAmount,
      average_transaction_amount: totalAmount / transactions.length,
      currency: mostCommonCurrency,
      earliest_transaction: dates[0],
      latest_transaction: dates[dates.length - 1],
      date_range_days: dates.length > 1 ? 
        Math.ceil((new Date(dates[dates.length - 1]).getTime() - new Date(dates[0]).getTime()) / (1000 * 60 * 60 * 24)) : 0,
      transaction_types: typeBreakdown,
      status_breakdown: statusBreakdown,
      common_line_items: commonLineItems,
      most_common_account: commonLineItems[0] ? {
        account_code: commonLineItems[0].account_code || '',
        frequency: commonLineItems[0].frequency,
      } : undefined,
    };
  }

  /**
   * Sanitize vendor name for security with comprehensive validation
   */
  private sanitizeVendorName(vendorName: string): string {
    // Validate input
    if (!vendorName || typeof vendorName !== 'string') {
      throw new Error('Invalid vendor name: must be a non-empty string');
    }
    
    // Comprehensive sanitization
    const sanitized = vendorName
      .replace(/[<>"';/\x00-\x1f\x7f-\x9f]/g, '') // Remove control chars and dangerous characters
      .replace(/\.\./g, '') // Prevent directory traversal
      .replace(/--/g, '') // Prevent SQL injection comments
      .replace(/\/\*/g, '') // Prevent SQL injection block comments
      .replace(/\*\//g, '') // Prevent SQL injection block comments
      .replace(/union\s+select/gi, '') // Prevent SQL injection
      .replace(/drop\s+table/gi, '') // Prevent SQL injection
      .trim()
      .substring(0, 100);
      
    // Validate result
    if (sanitized.length === 0) {
      throw new Error('Invalid vendor name: contains only invalid characters');
    }
    
    return sanitized;
  }

  /**
   * Rate limiting check for vendor history requests
   */
  private checkRateLimit(key: string): void {
    const now = Date.now();
    const resetTime = Math.floor(now / 60000) * 60000 + 60000; // Next minute boundary
    
    // Periodic cleanup to prevent memory leaks
    this.cleanupExpiredEntries();
    
    const existing = this.requestCounts.get(key);
    
    if (existing && existing.resetTime > now) {
      // Within current minute window
      if (existing.count >= this.MAX_REQUESTS_PER_MINUTE) {
        throw new Error('Rate limit exceeded. Please wait before making more requests.');
      }
      existing.count++;
    } else {
      // New minute window or expired
      this.requestCounts.set(key, { count: 1, resetTime });
    }
  }

  /**
   * Clean up expired entries from maps to prevent memory leaks
   */
  private cleanupExpiredEntries(): void {
    const now = Date.now();
    
    // Clean up rate limiting entries
    this.requestCounts.forEach((value, key) => {
      if (value.resetTime <= now) {
        this.requestCounts.delete(key);
      }
    });
    
    // Enforce max size limit for safety
    if (this.requestCounts.size > this.MAX_MAP_SIZE) {
      const sortedEntries = Array.from(this.requestCounts.entries())
        .sort(([, a], [, b]) => a.resetTime - b.resetTime);
      
      const toDelete = sortedEntries.slice(0, sortedEntries.length - this.MAX_MAP_SIZE);
      for (const [key] of toDelete) {
        this.requestCounts.delete(key);
      }
    }
    
    // Clean up vendor history requests that may be stuck
    if (this.vendorHistoryRequests.size > this.MAX_MAP_SIZE / 2) {
      console.warn('[ComplianceService] Vendor history requests map is large, clearing old entries');
      this.vendorHistoryRequests.clear();
    }
  }

  /**
   * Generate cache key for vendor history
   */
  private generateVendorHistoryCacheKey(
    entityId: string,
    vendorName: string,
    filters?: import('../types/vendor-history.types').VendorHistoryFilters
  ): string {
    const filterString = filters ? JSON.stringify(filters) : '';
    const rawKey = `vendor_history_${entityId}_${vendorName}_${filterString}`;
    
    // Create a hash for consistent key length
    const encoder = new TextEncoder();
    const data = encoder.encode(rawKey);
    return `vh_${data.length}_${rawKey.substring(0, 20)}`;
  }

  /**
   * Get vendor history from cache using SecureStorage
   */
  private getFromVendorHistoryCache(cacheKey: string): any | null {
    try {
      // SecureStorage is already imported at top of file
      const cached = SecureStorage.get(cacheKey, null);
      
      if (cached && cached.timestamp) {
        // VENDOR_HISTORY_CONFIG is already imported at top of file
        const now = Date.now();
        const isExpired = (now - cached.timestamp) > VENDOR_HISTORY_CONFIG.DEFAULT_TTL;
        
        if (!isExpired) {
          return cached.data;
        }
        
        // Remove expired cache
        SecureStorage.remove(cacheKey);
      }
    } catch (error) {
      console.warn('[ComplianceService] Cache retrieval failed:', error);
    }
    
    return null;
  }

  /**
   * Cache vendor history data using SecureStorage
   */
  private cacheVendorHistoryData(cacheKey: string, data: any): void {
    try {
      // SecureStorage is already imported at top of file
      const cacheData = {
        key: cacheKey,
        data,
        timestamp: Date.now(),
        ttl: 5 * 60 * 1000, // 5 minutes
      };
      
      SecureStorage.set(cacheKey, cacheData);
    } catch (error) {
      console.warn('[ComplianceService] Failed to cache vendor history data:', error);
      // Don't throw - caching failure shouldn't break the main functionality
    }
  }

  /**
   * Clear all vendor history cache data
   */
  public clearVendorHistoryCache(): void {
    try {
      // Get all keys from SecureStorage
      const allKeys = Object.keys(localStorage);
      const vendorHistoryKeys = allKeys.filter(key => key.startsWith('vh_'));
      
      console.log(`[ComplianceService] Clearing ${vendorHistoryKeys.length} vendor history cache entries`);
      
      vendorHistoryKeys.forEach(key => {
        SecureStorage.remove(key);
      });
      
      // Also clear in-flight requests
      this.vendorHistoryRequests.clear();
      
    } catch (error) {
      console.warn('[ComplianceService] Failed to clear vendor history cache:', error);
    }
  }

  /**
   * Get accounts from cache
   */
  private getFromAccountsCache(cacheKey: string): any[] | null {
    try {
      // SecureStorage is already imported at top of file
      const cached = SecureStorage.get(cacheKey, null);
      
      if (cached && cached.timestamp) {
        const now = Date.now();
        const ttl = 15 * 60 * 1000; // 15 minutes
        const isExpired = (now - cached.timestamp) > ttl;
        
        if (!isExpired) {
          return cached.accounts;
        }
        
        SecureStorage.remove(cacheKey);
      }
    } catch (error) {
      console.warn('[ComplianceService] Accounts cache retrieval failed:', error);
    }
    
    return null;
  }

  /**
   * Cache accounts data
   */
  private cacheAccountsData(cacheKey: string, accounts: any[]): void {
    try {
      // SecureStorage is already imported at top of file
      const cacheData = {
        entity_id: cacheKey.replace('chart_of_accounts_', ''),
        accounts,
        timestamp: Date.now(),
        ttl: 15 * 60 * 1000, // 15 minutes
      };
      
      SecureStorage.set(cacheKey, cacheData);
    } catch (error) {
      console.warn('[ComplianceService] Failed to cache accounts data:', error);
    }
  }

  /**
   * Get most frequent item from array
   */
  private getMostFrequent<T>(arr: T[]): T | undefined {
    if (arr.length === 0) return undefined;
    
    const counts = new Map<T, number>();
    let maxCount = 0;
    let mostFrequent: T | undefined;
    
    for (const item of arr) {
      const count = (counts.get(item) || 0) + 1;
      counts.set(item, count);
      
      if (count > maxCount) {
        maxCount = count;
        mostFrequent = item;
      }
    }
    
    return mostFrequent;
  }

  /**
   * Get compliance matrix summary for entity (two-tier architecture)
   * Returns aggregated counts by month and issue type for fast matrix display
   */
  async getMatrixSummary(
    entityId: string,
    filters: {
      issue_types?: string[];
      severity?: string[];
      status?: string[];
      date_from?: string;
      date_to?: string;
      signal?: AbortSignal;
    } = {}
  ): Promise<ComplianceMatrixSummary> {
    try {
      const queryParams = new URLSearchParams();
      
      // Apply filters to matrix summary
      if (filters.issue_types?.length) {
        filters.issue_types.forEach(type => queryParams.append('issue_types', type));
      }
      if (filters.severity?.length) {
        filters.severity.forEach(sev => queryParams.append('severity', sev));
      }
      if (filters.status?.length) {
        filters.status.forEach(stat => queryParams.append('status', stat));
      }
      if (filters.date_from) queryParams.append('date_from', filters.date_from);
      if (filters.date_to) queryParams.append('date_to', filters.date_to);

      const response = await api.get<ComplianceMatrixSummary>(
        `/compliance/v2/${entityId}/matrix-summary?${queryParams.toString()}`,
        filters.signal ? { signal: filters.signal } : undefined
      );

      console.log(`[ComplianceService] Matrix summary loaded for entity ${entityId}`);
      return response;
    } catch (error: any) {
      // Handle abort/cancel errors - re-throw for caller handling
      if (isRequestCancelled(error)) {
        console.log(`[ComplianceService] Matrix summary request aborted for entity ${entityId}`);
        throw error; // Re-throw so caller can handle it
      }

      console.error(`[ComplianceService] Error fetching matrix summary for ${entityId}:`, error);

      if (error.response?.status === 404) {
        throw new Error('Entity not found or compliance matrix data not available.');
      } else if (error.response?.status === 403) {
        throw new Error('Access denied. Please check your permissions.');
      } else {
        const message = error.response?.data?.detail || 'Failed to fetch compliance matrix summary';
        throw new Error(message);
      }
    }
  }

  /**
   * Get fast compliance counts by issue type (optimized for sidebar)
   * Uses Firestore aggregation COUNT - NO document streaming
   * Response time: ~500ms vs 12s for matrix-summary
   */
  async getCounts(
    entityId: string,
    filters: {
      status?: string[];
      date_from?: string;
      date_to?: string;
      signal?: AbortSignal;
    } = {}
  ): Promise<{
    success: boolean;
    entity_id: string;
    total_issues: number;
    issues_by_type: Record<string, number>;
    date_range: {
      from: string | null;
      to: string | null;
    };
    status_filters: string[];
    processing_time_ms: number;
  }> {
    try {
      const queryParams = new URLSearchParams();

      // Apply filters
      if (filters.status?.length) {
        filters.status.forEach(stat => queryParams.append('status', stat));
      }
      if (filters.date_from) queryParams.append('date_from', filters.date_from);
      if (filters.date_to) queryParams.append('date_to', filters.date_to);

      const response = await api.get<{
        success: boolean;
        entity_id: string;
        total_issues: number;
        issues_by_type: Record<string, number>;
        date_range: {
          from: string | null;
          to: string | null;
        };
        status_filters: string[];
        processing_time_ms: number;
      }>(
        `/compliance/v2/${entityId}/counts?${queryParams.toString()}`,
        filters.signal ? { signal: filters.signal } : undefined
      );

      console.log(`[ComplianceService] Fast counts loaded for entity ${entityId} in ${response.processing_time_ms}ms`);
      return response;
    } catch (error: any) {
      // Handle abort/cancel errors - re-throw for caller handling
      if (isRequestCancelled(error)) {
        console.log(`[ComplianceService] Counts request aborted for entity ${entityId}`);
        throw error;
      }

      console.error(`[ComplianceService] Error fetching counts for ${entityId}:`, error);

      if (error.response?.status === 404) {
        throw new Error('Entity not found or compliance data not available.');
      } else if (error.response?.status === 403) {
        throw new Error('Access denied. Please check your permissions.');
      } else {
        const message = error.response?.data?.detail || 'Failed to fetch compliance counts';
        throw new Error(message);
      }
    }
  }

  /**
   * Get issues for specific matrix cell (month + issue type combination)
   * Used for matrix cell drilling in two-tier architecture
   */
  async getCellIssues(
    entityId: string,
    month: string, // YYYY-MM format
    issueType: string,
    filters: {
      issue_types?: string[];
      severity?: string[];
      status?: string[];
      date_from?: string;
      date_to?: string;
      signal?: AbortSignal;
    } = {}
  ): Promise<ComplianceResponse> {
    try {
      const queryParams = new URLSearchParams();
      
      // Matrix cell drilling parameters
      queryParams.append('month', month);
      queryParams.append('cell_issue_type', issueType);
      
      // Apply existing filters (preserved from matrix view)
      if (filters.issue_types?.length) {
        filters.issue_types.forEach(type => queryParams.append('issue_types', type));
      }
      if (filters.severity?.length) {
        filters.severity.forEach(sev => queryParams.append('severity', sev));
      }
      if (filters.status?.length) {
        filters.status.forEach(stat => queryParams.append('status', stat));
      }
      if (filters.date_from) queryParams.append('date_from', filters.date_from);
      if (filters.date_to) queryParams.append('date_to', filters.date_to);

      const response = await api.get(
        `/compliance/v2/${entityId}/issues?${queryParams.toString()}`,
        filters.signal ? { signal: filters.signal } : undefined
      );

      // Validate and process the response 
      const validatedResponse = validateComplianceResponse(response);
      
      if (!validatedResponse) {
        throw new Error('Invalid response format from cell issues API');
      }
      
      const totalIssues = validatedResponse.active_issues.length + validatedResponse.dismissed_issues.length;
      console.log(
        `[ComplianceService] Cell issues loaded: ${totalIssues} issues ` +
        `for ${month} ${issueType} in entity ${entityId}`
      );
      
      return validatedResponse;
    } catch (error: any) {
      // Handle abort/cancel errors - re-throw for caller handling
      if (isRequestCancelled(error)) {
        console.log(
          `[ComplianceService] Cell issues request aborted for ${entityId} ` +
          `(${month}, ${issueType})`
        );
        throw error; // Re-throw so caller can handle it
      }

      console.error(
        `[ComplianceService] Error fetching cell issues for ${entityId} ` +
        `(${month}, ${issueType}):`, error
      );

      if (error.response?.status === 404) {
        throw new Error('Entity not found or compliance cell data not available.');
      } else if (error.response?.status === 403) {
        throw new Error('Access denied. Please check your permissions.');
      } else {
        const message = error.response?.data?.detail || 'Failed to fetch compliance cell issues';
        throw new Error(message);
      }
    }
  }

  // ========== BULK OPERATION HELPERS ==========

  /**
   * Core bulk operation implementation with chunked processing
   */
  private async performBulkOperation(
    entityId: string,
    issueHashes: string[],
    action: 'resolve' | 'dismiss',
    notes?: string,
    onProgress?: (completed: number, total: number, errors: any[]) => void
  ): Promise<BulkOperationResult> {
    try {
      // Input validation
      if (!entityId || typeof entityId !== 'string' || !entityId.trim()) {
        throw new Error('Invalid entity ID provided');
      }

      if (!issueHashes || !Array.isArray(issueHashes) || issueHashes.length === 0) {
        throw new Error('Issue hashes array is required and must not be empty');
      }

      // Validate all issue hashes
      const invalidHashes = issueHashes.filter(hash => !isValidIssueHash(hash));
      if (invalidHashes.length > 0) {
        throw new Error(`Invalid issue hash format: ${invalidHashes.join(', ')}`);
      }

      if (notes !== undefined && (typeof notes !== 'string' || notes.length > 2000)) {
        throw new Error(`${action === 'resolve' ? 'Resolution notes' : 'Dismissal reason'} must be a string with maximum 2000 characters.`);
      }

      console.log(`[ComplianceService] Starting bulk ${action} for ${issueHashes.length} issues in entity ${entityId}`);

      // Process in chunks to avoid overwhelming the backend
      const CHUNK_SIZE = 100;
      const chunks = this.chunkArray(issueHashes, CHUNK_SIZE);
      const errors: any[] = [];
      let totalSuccess = 0;
      let totalFailure = 0;
      const allResults: any[] = [];

      // Report initial progress (0 completed out of total)
      if (onProgress) {
        onProgress(0, issueHashes.length, []);
      }

      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];

        try {
          console.log(`[ComplianceService] Processing chunk ${i + 1}/${chunks.length} with ${chunk.length} issues`);

          const endpoint = action === 'resolve'
            ? `${this.baseUrl}/${entityId}/issues/bulk-resolve`
            : `${this.baseUrl}/${entityId}/issues/bulk-dismiss`;

          const payload = {
            issue_hashes: chunk,
            ...(notes && { [action === 'resolve' ? 'notes' : 'reason']: notes.trim() })
          };

          const response = await api.post<BulkOperationResult>(endpoint, payload);

          totalSuccess += response.success_count;
          totalFailure += response.failure_count;
          allResults.push(...response.results);

          // Track errors from this chunk
          const chunkErrors = response.results
            .filter(result => !result.success)
            .map(result => ({ issue_hash: result.issue_hash, error: result.error_message }));
          errors.push(...chunkErrors);

          // Report progress
          const completedSoFar = (i + 1) * CHUNK_SIZE;
          const actualCompleted = Math.min(completedSoFar, issueHashes.length);

          if (onProgress) {
            onProgress(actualCompleted, issueHashes.length, errors);
          }

          console.log(`[ComplianceService] Chunk ${i + 1} completed: ${response.success_count} success, ${response.failure_count} failure`);

        } catch (chunkError: any) {
          console.error(`[ComplianceService] Error processing chunk ${i + 1}:`, chunkError);

          // Mark all items in this chunk as failed
          const chunkFailures = chunk.map(hash => ({
            issue_hash: hash,
            success: false,
            error_message: chunkError.response?.data?.detail || chunkError.message || 'Unknown error'
          }));

          allResults.push(...chunkFailures);
          totalFailure += chunk.length;

          errors.push(...chunk.map(hash => ({ issue_hash: hash, error: chunkError.message })));

          // Continue processing remaining chunks
        }
      }

      const result: BulkOperationResult = {
        success_count: totalSuccess,
        failure_count: totalFailure,
        total_count: issueHashes.length,
        results: allResults,
        overall_success: totalFailure === 0
      };

      console.log(
        `[ComplianceService] Bulk ${action} completed: ` +
        `${totalSuccess} success, ${totalFailure} failure of ${issueHashes.length} total`
      );

      return result;

    } catch (error: any) {
      console.error(`[ComplianceService] Error in bulk ${action}:`, error);

      if (error.response?.status === 404) {
        throw new Error('Entity not found or bulk operation not available.');
      } else if (error.response?.status === 403) {
        throw new Error('Access denied. Please check your permissions.');
      } else {
        const message = error.response?.data?.detail || `Failed to perform bulk ${action} operation`;
        throw new Error(message);
      }
    }
  }

  /**
   * Bulk operation with comment creation (dual approach)
   */
  private async performBulkOperationWithComment(
    entityId: string,
    issueHashes: string[],
    action: 'resolve' | 'dismiss',
    userInput?: string,
    mentions?: string[],
    onProgress?: (completed: number, total: number, errors: any[]) => void
  ): Promise<BulkOperationResult> {
    try {
      console.log(`[ComplianceService] Starting bulk ${action} with comment for ${issueHashes.length} issues`);

      let plainTextNotes: string | undefined;

      // Process unified input if provided
      if (userInput && userInput.trim()) {
        const { processUnifiedMessage } = await import('../utils/message-processing');
        const processed = processUnifiedMessage(userInput);
        plainTextNotes = processed.plainText || undefined;
      }

      // Step 1: Perform bulk status change
      const statusResult = await this.performBulkOperation(
        entityId,
        issueHashes,
        action,
        plainTextNotes,
        onProgress
      );

      // Step 2: Create bulk comments for successful operations
      if (userInput && userInput.trim() && statusResult.success_count > 0) {
        try {
          const successfulHashes = statusResult.results
            .filter(result => result.success)
            .map(result => result.issue_hash);

          if (successfulHashes.length > 0) {
            await this.createBulkStatusChangeComments(
              entityId,
              successfulHashes,
              action,
              userInput.trim(),
              mentions
            );
          }
        } catch (commentError: any) {
          console.warn(`[ComplianceService] Bulk ${action} succeeded but comment creation failed:`, commentError);
          // Don't throw - status changes succeeded which is the primary operation
        }
      }

      console.log(`[ComplianceService] Bulk ${action} with comment completed successfully`);
      return statusResult;

    } catch (error: any) {
      console.error(`[ComplianceService] Error in bulk ${action} with comment:`, error);
      throw error;
    }
  }

  /**
   * Create bulk status change comments using the backend bulk comments endpoint
   */
  private async createBulkStatusChangeComments(
    entityId: string,
    issueHashes: string[],
    action: 'resolve' | 'dismiss',
    comment: string,
    mentions?: string[]
  ): Promise<void> {
    try {
      // SECURITY: Sanitize HTML before storing in comments
      const { stripHtmlAndDecode } = await import('../utils/message-processing');

      const safeComment = stripHtmlAndDecode(comment).trim();
      if (!safeComment) {
        console.warn('[ComplianceService] Comment was empty after HTML stripping');
        return;
      }

      // Prepare bulk comments data
      const comments = issueHashes.map(issueHash => ({
        issue_hash: issueHash,
        action_type: action,
        message: safeComment,
        mention_uids: mentions || []
      }));

      // Use the backend bulk comments endpoint
      const response = await api.post(`${this.baseUrl}/${entityId}/comments/bulk-create`, {
        comments
      });

      console.log(`[ComplianceService] Successfully created ${comments.length} bulk status change comments`);
    } catch (error: any) {
      console.error(`[ComplianceService] Error creating bulk status change comments:`, error);
      throw new Error(`Failed to create bulk comments: ${error.message}`);
    }
  }

  /**
   * Utility to chunk array into smaller arrays
   */
  private chunkArray<T>(array: T[], chunkSize: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  }

  // ========== ENHANCED RETRY OPERATIONS ==========

  /**
   * Enhanced bulk operation with automatic retry logic for failed items
   */
  private async performBulkOperationWithRetry(
    entityId: string,
    issueHashes: string[],
    action: 'resolve' | 'dismiss',
    notes?: string,
    onProgress?: (completed: number, total: number, errors: any[]) => void
  ): Promise<EnhancedBulkOperationResult> {
    const operationId = generateOperationId();

    try {
      console.log(`[ComplianceService] Starting enhanced bulk ${action} with retry support (${operationId})`);

      // First attempt with regular bulk operation
      const initialResult = await this.performBulkOperation(
        entityId,
        issueHashes,
        action,
        notes,
        onProgress
      );

      // If all succeeded, return success
      if (initialResult.overall_success) {
        clearPersistedRetryState(); // Clear any existing retry state
        return {
          ...initialResult,
          operation_id: operationId,
          retry_info: {
            total_retries: 0,
            max_retries_reached: false
          }
        };
      }

      // Analyze failures for retry eligibility
      const failedItems = initialResult.results.filter(r => !r.success);
      const retryableFailures = this.analyzeFailuresForRetry(failedItems);

      // If no retryable failures, return original result
      if (retryableFailures.length === 0) {
        return {
          ...initialResult,
          operation_id: operationId,
          retryable_failures: [],
          retry_info: {
            total_retries: 0,
            max_retries_reached: false
          }
        };
      }

      // Persist retry state for recovery
      const retryState: PersistedRetryState = {
        operationId,
        type: action === 'resolve' ? 'bulk_resolve' : 'bulk_dismiss',
        entityId,
        failedItems: retryableFailures.map(f => ({
          id: f.issue_hash,
          error: f.error_message
        })),
        timestamp: Date.now(),
        originalTotal: issueHashes.length,
        successCount: initialResult.success_count
      };

      persistRetryState(retryState);

      // Perform retry for failed items
      const retryResult = await this.retryFailedItemsInternal(
        entityId,
        retryableFailures,
        action,
        notes,
        onProgress
      );

      // Combine results
      const combinedResult = this.combineRetryResults(initialResult, retryResult);

      // Clear retry state on completion
      if (combinedResult.overall_success) {
        clearPersistedRetryState();
      }

      return {
        ...combinedResult,
        operation_id: operationId,
        retryable_failures: retryableFailures.filter(f =>
          !retryResult.results.some(r => r.issue_hash === f.issue_hash && r.success)
        ).map(f => ({
          issue_hash: f.issue_hash,
          error_type: this.classifyBulkError(f.error_message),
          can_retry: true,
          retry_after: 5000 // 5 seconds
        })),
        retry_info: {
          total_retries: 1,
          max_retries_reached: retryResult.failure_count > 0,
          next_retry_delay: retryResult.failure_count > 0 ? 10000 : undefined
        }
      };

    } catch (error: any) {
      console.error(`[ComplianceService] Enhanced bulk ${action} failed:`, error);
      throw error;
    }
  }

  /**
   * Enhanced bulk operation with comment creation and retry support
   */
  private async performBulkOperationWithCommentAndRetry(
    entityId: string,
    issueHashes: string[],
    action: 'resolve' | 'dismiss',
    userInput?: string,
    mentions?: string[],
    onProgress?: (completed: number, total: number, errors: any[]) => void
  ): Promise<EnhancedBulkOperationResult> {
    const operationId = generateOperationId();

    try {
      console.log(`[ComplianceService] Starting enhanced bulk ${action} with comment and retry support (${operationId})`);

      // First attempt with regular bulk operation with comment
      const initialResult = await this.performBulkOperationWithComment(
        entityId,
        issueHashes,
        action,
        userInput,
        mentions,
        onProgress
      );

      // Convert to enhanced result format
      const enhancedResult: EnhancedBulkOperationResult = {
        ...initialResult,
        operation_id: operationId
      };

      // If all succeeded, return success
      if (initialResult.overall_success) {
        clearPersistedRetryState();
        enhancedResult.retry_info = {
          total_retries: 0,
          max_retries_reached: false
        };
        return enhancedResult;
      }

      // For comment operations, we don't auto-retry due to complexity
      // Instead, we provide retry options for manual retry
      const failedItems = initialResult.results.filter(r => !r.success);
      const retryableFailures = this.analyzeFailuresForRetry(failedItems);

      enhancedResult.retryable_failures = retryableFailures.map(f => ({
        issue_hash: f.issue_hash,
        error_type: this.classifyBulkError(f.error_message),
        can_retry: true,
        retry_after: 5000
      }));

      enhancedResult.retry_info = {
        total_retries: 0,
        max_retries_reached: false,
        next_retry_delay: 5000
      };

      // Persist retry state for manual retry
      if (retryableFailures.length > 0) {
        const retryState: PersistedRetryState = {
          operationId,
          type: action === 'resolve' ? 'bulk_resolve' : 'bulk_dismiss',
          entityId,
          failedItems: retryableFailures.map(f => ({
            id: f.issue_hash,
            error: f.error_message
          })),
          timestamp: Date.now(),
          originalTotal: issueHashes.length,
          successCount: initialResult.success_count
        };

        persistRetryState(retryState);
      }

      return enhancedResult;

    } catch (error: any) {
      console.error(`[ComplianceService] Enhanced bulk ${action} with comment failed:`, error);
      throw error;
    }
  }

  /**
   * Retry specific failed items from a previous bulk operation
   */
  async retryFailedItems(
    entityId: string,
    failedItems: Array<{ issue_hash: string; error_message?: string }>,
    action: 'resolve' | 'dismiss',
    notes?: string,
    options: RetryOperationOptions = {}
  ): Promise<BulkRetryResult> {
    try {
      console.log(`[ComplianceService] Retrying ${failedItems.length} failed items for ${action}`);

      const retryResult = await retryFailedItems(
        failedItems.map(item => ({ id: item.issue_hash, error: item.error_message })),
        async (item) => {
          // Retry individual operation with exponential backoff
          const retryWithBackoffResult = await retryWithBackoff(
            async () => {
              if (action === 'resolve') {
                await this.resolveIssue(entityId, item.id, notes);
              } else {
                await this.dismissIssue(entityId, item.id, notes);
              }
            },
            {
              signal: options.signal,
              onRetry: (attempt) => {
                console.log(`Retrying ${action} for ${item.id} (attempt ${attempt.attemptNumber})`);
              }
            }
          );

          if (!retryWithBackoffResult.success) {
            throw new Error(retryWithBackoffResult.error?.userMessage || 'Retry failed');
          }
        },
        {
          onProgress: options.onProgress,
          signal: options.signal
        }
      );

      // Clear retry state if all succeeded
      if (retryResult.overall_success) {
        clearPersistedRetryState();
      }

      console.log(`[ComplianceService] Retry completed: ${retryResult.success_count} success, ${retryResult.failure_count} failure`);
      return retryResult;

    } catch (error: any) {
      console.error(`[ComplianceService] Retry operation failed:`, error);
      throw error;
    }
  }

  /**
   * Get persisted retry state for recovery
   */
  getPersistedRetryState(): PersistedRetryState | null {
    return getPersistedRetryState();
  }

  /**
   * Clear persisted retry state
   */
  clearRetryState(): void {
    clearPersistedRetryState();
  }

  /**
   * Check if there are retryable operations available
   */
  hasRetryableOperations(): boolean {
    const state = getPersistedRetryState();
    return state !== null && state.failedItems.length > 0;
  }

  // ========== PRIVATE RETRY HELPERS ==========

  /**
   * Analyze failed items to determine retry eligibility
   */
  private analyzeFailuresForRetry(
    failedItems: Array<{ issue_hash: string; error_message?: string }>
  ): Array<{ issue_hash: string; error_message?: string }> {
    return failedItems.filter(item => {
      const classified = classifyError({ message: item.error_message });
      return classified.isRetryable;
    });
  }

  /**
   * Classify bulk operation errors
   */
  private classifyBulkError(errorMessage?: string): ErrorType {
    const classified = classifyError({ message: errorMessage });
    return classified.type;
  }

  /**
   * Retry failed items internally with exponential backoff
   */
  private async retryFailedItemsInternal(
    entityId: string,
    failedItems: Array<{ issue_hash: string; error_message?: string }>,
    action: 'resolve' | 'dismiss',
    notes?: string,
    onProgress?: (completed: number, total: number, errors: any[]) => void
  ): Promise<BulkRetryResult> {
    const RETRY_CHUNK_SIZE = 5; // Smaller chunks for retry operations
    const chunks = this.chunkArray(failedItems, RETRY_CHUNK_SIZE);
    const errors: any[] = [];
    let totalSuccess = 0;
    let totalFailure = 0;
    const allResults: any[] = [];

    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];

      for (const item of chunk) {
        try {
          const retryResult = await retryWithBackoff(
            async () => {
              if (action === 'resolve') {
                await this.resolveIssue(entityId, item.issue_hash, notes);
              } else {
                await this.dismissIssue(entityId, item.issue_hash, notes);
              }
            },
            {
              onRetry: (attempt) => {
                console.log(`Retrying ${action} for ${item.issue_hash} (attempt ${attempt.attemptNumber})`);
              }
            }
          );

          if (retryResult.success) {
            allResults.push({
              issue_hash: item.issue_hash,
              success: true,
              attempts: retryResult.attempts
            });
            totalSuccess++;
          } else {
            allResults.push({
              issue_hash: item.issue_hash,
              success: false,
              error_message: retryResult.error?.userMessage || 'Retry failed',
              attempts: retryResult.attempts
            });
            totalFailure++;
            errors.push({ issue_hash: item.issue_hash, error: retryResult.error?.userMessage });
          }

        } catch (error: any) {
          const classified = classifyError(error);
          allResults.push({
            item_id: item.issue_hash,
            success: false,
            error_message: classified.userMessage,
            attempts: 1
          });
          totalFailure++;
          errors.push({ issue_hash: item.issue_hash, error: classified.userMessage });
        }

        // Report progress after each item
        if (onProgress) {
          const completed = totalSuccess + totalFailure;
          onProgress(completed, failedItems.length, errors);
        }
      }
    }

    return {
      success_count: totalSuccess,
      failure_count: totalFailure,
      total_count: failedItems.length,
      results: allResults,
      overall_success: totalFailure === 0
    };
  }

  /**
   * Combine initial and retry results
   */
  private combineRetryResults(
    initialResult: BulkOperationResult,
    retryResult: BulkRetryResult
  ): BulkOperationResult {
    // Update the failed items that were successfully retried
    const updatedResults = initialResult.results.map(result => {
      if (!result.success) {
        const retryItem = retryResult.results.find(r => r.issue_hash === result.issue_hash);
        if (retryItem && retryItem.success) {
          return {
            ...result,
            success: true,
            error_message: undefined
          };
        }
      }
      return result;
    });

    const newSuccessCount = updatedResults.filter(r => r.success).length;
    const newFailureCount = updatedResults.filter(r => !r.success).length;

    return {
      success_count: newSuccessCount,
      failure_count: newFailureCount,
      total_count: initialResult.total_count,
      results: updatedResults,
      overall_success: newFailureCount === 0
    };
  }

  /**
   * Get transaction attachments from Firestore cache (fast, no Xero API call)
   */
  async getTransactionAttachments(
    transactionId: string,
    entityId: string
  ): Promise<TransactionAttachmentsResponse> {
    // api.get already returns response.data, not the full response
    return await api.get<TransactionAttachmentsResponse>(
      `/compliance/v2/transactions/${transactionId}/attachments`,
      { params: { entity_id: entityId } }
    );
  }

  /**
   * Lazy-load transaction attachments from Xero API
   */
  async lazyLoadAttachments(
    transactionId: string,
    entityId: string,
    transactionType: string,
    forceReload: boolean = false,
    signal?: AbortSignal
  ): Promise<TransactionAttachmentsResponse> {
    // api.post already returns response.data, not the full response
    return await api.post<TransactionAttachmentsResponse>(
      `/compliance/v2/transactions/${transactionId}/lazy-load-attachments`,
      {
        entity_id: entityId,
        transaction_type: transactionType,
        force_reload: forceReload
      },
      { signal }
    );
  }

  /**
   * Batch check attachment status for multiple transactions
   * Handles chunking for backend limit of 200 transaction IDs per request
   * Returns Map<transaction_id, has_attachments> for efficient lookups
   * Uses Promise.allSettled for graceful degradation on partial failures
   */
  async batchCheckAttachments(
    transactionIds: string[],
    entityId: string,
    signal?: AbortSignal
  ): Promise<Map<string, boolean>> {
    try {
      // Validate inputs
      if (!entityId || typeof entityId !== 'string') {
        console.error('[ComplianceService] Invalid entity ID for batch attachment check');
        return new Map();
      }

      if (transactionIds.length === 0) {
        return new Map();
      }

      // Deduplicate transaction IDs
      const uniqueIds = [...new Set(transactionIds)];
      if (uniqueIds.length !== transactionIds.length) {
        console.warn(
          `[ComplianceService] Removed ${transactionIds.length - uniqueIds.length} duplicate transaction IDs`
        );
      }

      const CHUNK_SIZE = 200; // Backend limit per request
      const chunks: string[][] = [];

      // Split transaction IDs into chunks of 200
      for (let i = 0; i < uniqueIds.length; i += CHUNK_SIZE) {
        chunks.push(uniqueIds.slice(i, i + CHUNK_SIZE));
      }

      console.log(
        `[ComplianceService] Processing ${uniqueIds.length} transactions in ${chunks.length} batch(es) for entity ${entityId}`
      );

      const startTime = performance.now();

      // Process all chunks in parallel
      const chunkPromises = chunks.map(async (chunk) => {
        if (signal?.aborted) {
          throw new Error('AbortError');
        }

        const response = await api.post<BatchAttachmentCheckResponse>(
          '/compliance/v2/attachments/batch-check',
          {
            transaction_ids: chunk,
            entity_id: entityId
          },
          { signal }
        );

        // Validate response structure
        if (!response || typeof response !== 'object' || !response.results) {
          console.warn('[ComplianceService] Invalid batch attachment response structure:', response);
          return { results: {}, checked_count: 0, failed_batch_count: 0 };
        }

        return response;
      });

      // Use allSettled for graceful degradation - partial failures are acceptable
      const results = await Promise.allSettled(chunkPromises);

      // Merge all chunk results into single Map, handling partial failures
      const attachmentStatusMap = new Map<string, boolean>();
      let totalChecked = 0;
      let totalFailed = 0;
      let failedBatches = 0;

      results.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value) {
          totalChecked += result.value.checked_count;
          totalFailed += result.value.failed_batch_count;

          // Convert Record<string, boolean> to Map entries
          Object.entries(result.value.results).forEach(([transactionId, hasAttachments]) => {
            attachmentStatusMap.set(transactionId, hasAttachments);
          });
        } else if (result.status === 'rejected') {
          // Don't count cancelled requests as failed batches
          if (!isRequestCancelled(result.reason)) {
            failedBatches++;
            console.warn(
              `[ComplianceService] Attachment batch ${index + 1}/${chunks.length} failed:`,
              result.reason
            );
          }
        }
      });

      const duration = Math.round(performance.now() - startTime);

      // Log partial failure warning if some batches failed
      if (failedBatches > 0) {
        if (failedBatches < chunks.length) {
          console.warn(
            `[ComplianceService] Partial results: ${failedBatches}/${chunks.length} batches failed, showing ${attachmentStatusMap.size} available attachment statuses`
          );
        } else {
          console.error(
            `[ComplianceService] All ${chunks.length} batches failed - no attachment data available`
          );
          // Only show toast for complete failures
          toast.error('Failed to check attachment status');
        }
      }

      console.log(
        `[ComplianceService] Batch attachment check completed in ${duration}ms: ${totalChecked} transactions checked, ${attachmentStatusMap.size} statuses returned, ${totalFailed} backend failures across ${chunks.length - failedBatches}/${chunks.length} batches`
      );

      return attachmentStatusMap;
    } catch (error: any) {
      // Don't log or show errors for cancelled requests - they're expected during cleanup
      if (isRequestCancelled(error)) {
        console.log('[ComplianceService] Batch attachment check request aborted');
        return new Map();
      }

      console.error('[ComplianceService] Batch attachment check failed:', error);
      toast.error(
        error.response?.data?.detail ||
        error.message ||
        'Failed to check attachment status'
      );

      // Return empty map on error - individual statuses will show as unknown
      return new Map();
    }
  }

  /**
   * Get Type F (Aged Payables) compliance issues for an entity
   *
   * @param entityId - Xero entity ID
   * @param thresholdDays - Optional minimum days overdue filter (client-side filtering)
   * @param signal - Optional AbortSignal for request cancellation
   * @returns Array of compliance issues with AgedPayableMetadata
   */
  async getAgedPayables(
    entityId: string,
    thresholdDays?: number,
    signal?: AbortSignal
  ): Promise<ComplianceIssue[]> {
    try {
      // Validate inputs
      if (!entityId || typeof entityId !== 'string' || !entityId.trim()) {
        throw new Error('Invalid entity ID provided');
      }

      if (thresholdDays !== undefined && (typeof thresholdDays !== 'number' || thresholdDays < 0)) {
        throw new Error('Threshold days must be a non-negative number');
      }

      console.log(`[ComplianceService] Fetching aged payables for entity ${entityId}` +
        (thresholdDays !== undefined ? ` with threshold ${thresholdDays} days` : ''));

      // Use getEntityIssues with issue_type filter for Type F
      const response = await this.getEntityIssues(entityId, {
        issue_types: ['F']
      });

      let issues = response.active_issues || [];

      // Client-side threshold filtering if specified
      if (thresholdDays !== undefined && issues.length > 0) {
        const originalCount = issues.length;
        issues = issues.filter((issue: ComplianceIssue) => {
          const metadata = issue.metadata as AgedPayableMetadata;
          return metadata && metadata.days_overdue >= thresholdDays;
        });

        console.log(
          `[ComplianceService] Filtered aged payables: ${originalCount} → ${issues.length} ` +
          `(threshold: ${thresholdDays} days)`
        );
      }

      console.log(`[ComplianceService] Successfully loaded ${issues.length} aged payables for entity ${entityId}`);

      return issues;
    } catch (error: any) {
      // Handle abort/cancel errors - re-throw for caller handling
      if (isRequestCancelled(error)) {
        console.log(`[ComplianceService] Aged payables request aborted for entity ${entityId}`);
        throw error;
      }

      console.error(`[ComplianceService] Error fetching aged payables for ${entityId}:`, error);

      if (error.response?.status === 404) {
        throw new Error('Entity not found or aged payables data not available.');
      } else if (error.response?.status === 403) {
        throw new Error('Access denied. Please check your permissions.');
      } else {
        const message = error.response?.data?.detail || error.message || 'Failed to fetch aged payables';
        throw new Error(message);
      }
    }
  }

  /**
   * Snooze a single compliance issue
   *
   * @param entityId - Entity ID
   * @param issueHash - Issue hash to snooze
   * @param snoozeUntilDate - Date string (YYYY-MM-DD) when snooze expires
   * @param reason - Optional reason for snoozing
   * @param signal - Optional AbortSignal for request cancellation
   * @returns Updated issue with snooze fields
   */
  async snoozeIssue(
    entityId: string,
    issueHash: string,
    snoozeUntilDate: string,
    reason?: string,
    signal?: AbortSignal
  ): Promise<ComplianceIssue> {
    try {
      // Validate inputs
      if (!entityId || typeof entityId !== 'string' || !entityId.trim()) {
        throw new Error('Invalid entity ID provided');
      }

      if (!issueHash || typeof issueHash !== 'string' || !issueHash.trim()) {
        throw new Error('Invalid issue hash provided');
      }

      if (!snoozeUntilDate || typeof snoozeUntilDate !== 'string' || !snoozeUntilDate.trim()) {
        throw new Error('Invalid snooze until date provided');
      }

      // Frontend validation: Ensure date is in the future
      const snoozeDate = new Date(snoozeUntilDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (snoozeDate <= today) {
        throw new Error('Snooze date must be in the future');
      }

      // Frontend validation: Ensure date is within 90 days (matches backend exactly)
      const maxDate = new Date();
      maxDate.setDate(maxDate.getDate() + 90);
      maxDate.setHours(23, 59, 59, 999);

      if (snoozeDate > maxDate) {
        throw new Error('Snooze date cannot be more than 90 days in the future');
      }

      const response = await api.post<{ success: boolean; entity_id: string; issue: ComplianceIssue; message: string }>(
        `/compliance/v2/${entityId}/issues/snooze`,
        {
          issue_hash: issueHash,
          snooze_until: snoozeUntilDate,  // Matches backend Pydantic field name
          reason: reason || undefined
        },
        { signal }
      );

      return response.issue;  // Return the updated issue object
    } catch (error: any) {
      // Handle abort/cancel errors
      if (isRequestCancelled(error)) {
        throw error;
      }

      console.error(`[ComplianceService] Error snoozing issue ${issueHash}:`, error);

      // Extract user-friendly error message
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to snooze issue';
      throw new Error(errorMessage);
    }
  }

  /**
   * Bulk snooze multiple compliance issues
   *
   * @param entityId - Entity ID
   * @param issueHashes - Array of issue hashes to snooze
   * @param snoozeUntilDate - Date string (YYYY-MM-DD) when snooze expires
   * @param reason - Optional reason for snoozing
   * @param onProgress - Optional progress callback (processed, total, current_hash)
   * @param signal - Optional AbortSignal for request cancellation
   * @returns Object with processed count and results array
   */
  async bulkSnoozeIssues(
    entityId: string,
    issueHashes: string[],
    snoozeUntilDate: string,
    reason?: string,
    onProgress?: (processed: number, total: number, currentHash?: string) => void,
    signal?: AbortSignal
  ): Promise<{
    processed: number;
    results: Array<{ issue_hash: string; success: boolean; error?: string }>;
  }> {
    try {
      // Validate inputs
      if (!entityId || typeof entityId !== 'string' || !entityId.trim()) {
        throw new Error('Invalid entity ID provided');
      }

      if (!Array.isArray(issueHashes) || issueHashes.length === 0) {
        throw new Error('No issue hashes provided');
      }

      if (!snoozeUntilDate || typeof snoozeUntilDate !== 'string' || !snoozeUntilDate.trim()) {
        throw new Error('Invalid snooze until date provided');
      }

      // Frontend validation: Ensure date is in the future
      const snoozeDate = new Date(snoozeUntilDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (snoozeDate <= today) {
        throw new Error('Snooze date must be in the future');
      }

      // Frontend validation: Ensure date is within 90 days (matches backend exactly)
      const maxDate = new Date();
      maxDate.setDate(maxDate.getDate() + 90);
      maxDate.setHours(23, 59, 59, 999);

      if (snoozeDate > maxDate) {
        throw new Error('Snooze date cannot be more than 90 days in the future');
      }

      const response = await api.post<{
        processed: number;
        results: Array<{ issue_hash: string; success: boolean; error?: string }>;
      }>(
        `/compliance/v2/${entityId}/issues/bulk-snooze`,
        {
          issue_hashes: issueHashes,
          snooze_until: snoozeUntilDate,  // Matches backend Pydantic field name
          reason: reason || undefined
        },
        { signal }
      );

      // Call progress callback with final count if provided
      if (onProgress) {
        onProgress(response.processed, issueHashes.length);
      }

      return response;
    } catch (error: any) {
      // Handle abort/cancel errors
      if (isRequestCancelled(error)) {
        throw error;
      }

      console.error(`[ComplianceService] Error bulk snoozing issues:`, error);

      // Extract user-friendly error message
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to snooze issues';
      throw new Error(errorMessage);
    }
  }

  /**
   * Unsnooze (clear snooze) a single compliance issue
   *
   * @param entityId - Entity ID
   * @param issueHash - Issue hash to unsnooze
   * @param signal - Optional AbortSignal for request cancellation
   * @returns Updated issue with snooze fields cleared
   */
  async unsnoozeIssue(
    entityId: string,
    issueHash: string,
    signal?: AbortSignal
  ): Promise<ComplianceIssue> {
    try {
      // Validate inputs
      if (!entityId || typeof entityId !== 'string' || !entityId.trim()) {
        throw new Error('Invalid entity ID provided');
      }

      if (!issueHash || typeof issueHash !== 'string' || !issueHash.trim()) {
        throw new Error('Invalid issue hash provided');
      }

      console.log(`[ComplianceService] Unsnoozing issue ${issueHash}`);

      const response = await api.post<ComplianceIssue>(
        `/compliance/v2/${entityId}/issues/unsnooze`,
        {
          issue_hash: issueHash
        },
        { signal }
      );

      return response;
    } catch (error: any) {
      // Handle abort/cancel errors
      if (isRequestCancelled(error)) {
        throw error;
      }

      console.error(`[ComplianceService] Error unsnoozing issue ${issueHash}:`, error);

      // Extract user-friendly error message
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to unsnooze issue';
      throw new Error(errorMessage);
    }
  }

  /**
   * Bulk unsnooze multiple compliance issues
   *
   * @param entityId - Entity ID
   * @param issueHashes - Array of issue hashes to unsnooze
   * @param signal - Optional AbortSignal for request cancellation
   * @returns Object with processed count and results array
   */
  async bulkUnsnoozeIssues(
    entityId: string,
    issueHashes: string[],
    signal?: AbortSignal
  ): Promise<{
    processed: number;
    results: Array<{ issue_hash: string; success: boolean; error?: string }>;
  }> {
    try {
      // Validate inputs
      if (!entityId || typeof entityId !== 'string' || !entityId.trim()) {
        throw new Error('Invalid entity ID provided');
      }

      if (!Array.isArray(issueHashes) || issueHashes.length === 0) {
        throw new Error('No issue hashes provided');
      }

      console.log(`[ComplianceService] Bulk unsnoozing ${issueHashes.length} issues`);

      const response = await api.post<{
        processed: number;
        results: Array<{ issue_hash: string; success: boolean; error?: string }>;
      }>(
        `/compliance/v2/${entityId}/issues/bulk-unsnooze`,
        {
          issue_hashes: issueHashes
        },
        { signal }
      );

      return response;
    } catch (error: any) {
      // Handle abort/cancel errors
      if (isRequestCancelled(error)) {
        throw error;
      }

      console.error(`[ComplianceService] Error bulk unsnoozing issues:`, error);

      // Extract user-friendly error message
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to unsnooze issues';
      throw new Error(errorMessage);
    }
  }

  /**
   * Helper function to check if an issue is currently snoozed
   *
   * @param issue - Compliance issue to check
   * @returns True if issue is currently snoozed, false otherwise
   */
  isIssueSnoozed(issue: ComplianceIssue): boolean {
    // Check if snooze_until_date exists and is in the future
    if (!issue.snooze_until_date) {
      return false;
    }

    try {
      const snoozeUntil = new Date(issue.snooze_until_date);
      const now = new Date();

      // Issue is snoozed if snooze date is in the future
      return snoozeUntil > now;
    } catch (error) {
      console.warn('[ComplianceService] Invalid snooze_until_date format:', issue.snooze_until_date);
      return false;
    }
  }
}

export const complianceService = new ComplianceService();